import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Badge, Modal, Table, Spinner, InputGroup, FormControl, Toast, ToastContainer, Form, ProgressBar } from 'react-bootstrap';
import { getApiUrl } from '../config/api';

/* ──────────────────────────────────────────────────────────────
   DESIGN TOKENS (Matching TrsLaboratorium & Master Themes)
────────────────────────────────────────────────────────────── */
const T = {
  navH    : 50,
  radius  : 8,
  border  : '#cbd5e1',
  muted   : '#64748b',
  navy    : '#0f172a',
  blueDark: '#002b49',
  blueHdr : '#004b87',
  blueSoft: '#e0f2fe',
  sky     : '#0ea5e9',
  tealHdr : '#007580',
  tealSub : '#005f69',
  tealRow : '#e6f7f8',
  slate   : '#f8fafc',
};

const fmt = (v) => (parseFloat(v) || 0).toLocaleString('id-ID');

const TarifLab = () => {
  const navigate = useNavigate();

  // API URL Helper
  const apiUrl = getApiUrl() || 'http://localhost:8000';

  // ── Master Options States ──
  const [klasifikasiOpts, setKlasifikasiOpts] = useState([]);
  const [kelasOpts, setKelasOpts]             = useState([]);
  const [jenisLabOpts, setJenisLabOpts]       = useState([]);
  const [tindakanLabList, setTindakanLabList] = useState([]);

  // ── Selected Header Filter States ──
  const [selectedKlasifikasi, setSelectedKlasifikasi] = useState({ kd: '001', nm: 'UMUM / PRIBADI' });
  const [selectedKelas, setSelectedKelas]             = useState({ kd: '001', nm: 'Rawat Jalan UGD' });
  const [selectedJenis, setSelectedJenis]             = useState({ kd: '', nm: '' });

  // ── Table Data & Loading ──
  const [tarifList, setTarifList]   = useState([]);
  const [isLoading, setIsLoading]   = useState(false);
  const [searchFilter, setSearchFilter] = useState('');

  // ── Pagination States ──
  const [currentPage, setCurrentPage]   = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // ── Row Editor State (Matching Image Top Table Inputs) ──
  const [editor, setEditor] = useState({
    kd_tindakan: '',
    nm_tindakan: '',
    biayadasar: 0,
    alat: 0,
    bentuk: '-',
    bahan: 0,
    biaya: 0,
    isEdit: false
  });

  // ── Toast Notification State ──
  const [toastNotif, setToastNotif] = useState({
    show: false,
    type: 'info',
    title: '',
    message: ''
  });

  const notify = (type, title, message) => {
    setToastNotif({ show: true, type, title, message });
  };

  // ── Modal Rincian Bahan State ──
  const [showRincianBahanModal, setShowRincianBahanModal] = useState(false);
  const [rincianBahanList, setRincianBahanList]           = useState([]);
  const [isRincianLoading, setIsRincianLoading]           = useState(false);
  const [selectedBahanItem, setSelectedBahanItem]         = useState(null);

  // ── Rincian Bahan Input Row State ──
  const [bahanInput, setBahanInput] = useState({
    kd_obat: '',
    nm_obat: '',
    satuan: '',
    hargadasar: 0,
    margin: 0,
    qty: 1,
    harga: 0
  });

  // Calculate nilai bahan: (HD + (HD * margin / 100)) * qty
  useEffect(() => {
    const hd = parseFloat(bahanInput.hargadasar) || 0;
    const mg = parseFloat(bahanInput.margin) || 0;
    const qt = parseFloat(bahanInput.qty) || 0;
    const totalNilai = (hd + (hd * mg / 100.0)) * qt;
    setBahanInput(prev => ({ ...prev, harga: totalNilai }));
  }, [bahanInput.hargadasar, bahanInput.margin, bahanInput.qty]);

  // ── Modal Salin Tarif State ──
  const [showSalinModal, setShowSalinModal] = useState(false);
  const [salinPayload, setSalinPayload]     = useState({
    src_klasifikasi: '',
    src_kelas: '',
    src_jenis: '',
    dst_klasifikasi: '',
    dst_kelas: '',
    dst_jenis: '',
    mode_kenaikan: false,
    kenaikan_persen: 0
  });
  const [isSalinLoading, setIsSalinLoading] = useState(false);
  const [salinProgress, setSalinProgress]   = useState(0);

  // ── Tab Navigation State ──
  const [activeTab, setActiveTab] = useState('REGULER'); // 'REGULER' | 'PAKET'

  // ── Modal Master Tindakan Lab Lookup ──
  const [showLookupModal, setShowLookupModal] = useState(false);
  const [lookupSearch, setLookupSearch]       = useState('');
  const [lookupTarget, setLookupTarget]       = useState('REGULER'); // 'REGULER' | 'PAKET'

  // ── State for Tarif Paket Laboratorium ──
  const [paketTarifList, setPaketTarifList]         = useState([]);
  const [isPaketLoading, setIsPaketLoading]         = useState(false);
  const [selectedPaketJenis, setSelectedPaketJenis] = useState({ kd: '', nm: '' });
  const [selectedPaket]                             = useState({ kd: '', nm: '' });
  const [paketInput, setPaketInput]                 = useState({
    kd_tindakan: '',
    nm_tindakan: '',
    biayadasar: 0,
    alat: 0,
    bentuk: '-',
    bahan: 0,
    biaya: 0
  });

  // Calculate paket total biaya: biayadasar + alat + bahan
  useEffect(() => {
    const bd = parseFloat(paketInput.biayadasar) || 0;
    const al = parseFloat(paketInput.alat) || 0;
    const bh = parseFloat(paketInput.bahan) || 0;
    setPaketInput(prev => ({ ...prev, biaya: bd + al + bh }));
  }, [paketInput.biayadasar, paketInput.alat, paketInput.bahan]);

  // ── State for Tarif Rujukan Laboratorium ──
  const [labRujukanList, setLabRujukanList]         = useState([]);
  const [selectedLabRujukan, setSelectedLabRujukan] = useState({ kd: '', nm: '' });
  const [selectedRujukanJenis, setSelectedRujukanJenis] = useState({ kd: '', nm: '' });
  const [rujukanTarifList, setRujukanTarifList]     = useState([]);
  const [isRujukanLoading, setIsRujukanLoading]     = useState(false);
  const [rujukanInput, setRujukanInput]             = useState({
    kd_tindakan: '',
    nm_tindakan: '',
    biaya: 0,
    discrp: 0,
    disc: 0,
    harga: 0
  });

  // Calculate rujukan total harga nett: biaya - discrp
  useEffect(() => {
    const b = parseFloat(rujukanInput.biaya) || 0;
    const drp = parseFloat(rujukanInput.discrp) || 0;
    const nett = Math.max(0, b - drp);
    setRujukanInput(prev => ({ ...prev, harga: nett }));
  }, [rujukanInput.biaya, rujukanInput.discrp]);

  // ── Focus Ref & Modal Refs ──
  const kdInputRef = useRef(null);
  const showRincianBahanRef = useRef(false);
  const showSalinRef = useRef(false);
  const showLookupRef = useRef(false);

  // ── Modal Print/Cetak State ──
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printPdfUrl, setPrintPdfUrl]       = useState('');
  const [isPdfLoading, setIsPdfLoading]     = useState(false);
  const [pdfProgress, setPdfProgress]       = useState(0);
  const showPrintRef = useRef(false);

  useEffect(() => {
    showRincianBahanRef.current = showRincianBahanModal;
  }, [showRincianBahanModal]);

  useEffect(() => {
    showSalinRef.current = showSalinModal;
  }, [showSalinModal]);

  useEffect(() => {
    showLookupRef.current = showLookupModal;
  }, [showLookupModal]);

  useEffect(() => {
    showPrintRef.current = showPrintModal;
  }, [showPrintModal]);

  // ── Keyboard Shortcut [Esc] to Exit / Go Back ──
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (showPrintRef.current) {
          e.preventDefault();
          e.stopPropagation();
          setShowPrintModal(false);
          return;
        }
        if (showRincianBahanRef.current) {
          e.preventDefault();
          e.stopPropagation();
          setShowRincianBahanModal(false);
          return;
        }
        if (showSalinRef.current) {
          e.preventDefault();
          e.stopPropagation();
          setShowSalinModal(false);
          return;
        }
        if (showLookupRef.current) {
          e.preventDefault();
          e.stopPropagation();
          setShowLookupModal(false);
          return;
        }
        // Jika tidak ada modal yang terbuka, baru kembali ke halaman home
        navigate('/home');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);

  // ── Fetch Master Dropdown Options ──
  useEffect(() => {
    const fetchMasterData = async () => {
      try {
        // 1. Klasifikasi (ambil dari endpoint show_klasifikasi dengan field kd_klas & nm_klas)
        const resKlas = await fetch(`${apiUrl}/show_klasifikasi`);
        const jsonKlas = await resKlas.json();
        const listKlas = jsonKlas.list_klasifikasi || jsonKlas.data || [];
        setKlasifikasiOpts(listKlas);
        if (listKlas.length > 0) {
          const first = listKlas[0];
          setSelectedKlasifikasi({
            kd: first.kd_klas || first.kd_klasifikasi || first.kode || '001',
            nm: first.nm_klas || first.nm_klasifikasi || first.nama || 'UMUM'
          });
        }

        // 2. Kelas (ambil dari endpoint show_kelas dengan field kd_klas & nm_klas)
        const resKelas = await fetch(`${apiUrl}/show_kelas`);
        const jsonKelas = await resKelas.json();
        const listKelas = jsonKelas.list_kelas || jsonKelas.data || [];
        setKelasOpts(listKelas);
        if (listKelas.length > 0) {
          const firstK = listKelas[0];
          setSelectedKelas({
            kd: firstK.kd_klas || firstK.kd_kelas || firstK.kode || '001',
            nm: firstK.nm_klas || firstK.nm_kelas || firstK.nama || 'Rawat Jalan'
          });
        }

        // 3. Jenis Tindakan Lab
        const resJenis = await fetch(`${apiUrl}/jenis_tindakan_lab`);
        const jsonJenis = await resJenis.json();
        const listJenis = jsonJenis.list_jenis_tindakan_lab || jsonJenis.data || [];
        setJenisLabOpts(listJenis);

        // 4. Master Tindakan Lab
        const resTindakan = await fetch(`${apiUrl}/show_tindakan_lab`);
        const jsonTindakan = await resTindakan.json();
        const listTindakan = jsonTindakan.list_tindakan_lab || jsonTindakan.data || [];
        setTindakanLabList(listTindakan);
      } catch (err) {
        console.error('Error fetching master data:', err);
      }
    };
    fetchMasterData();
  }, [apiUrl]);

  // ── Fetch Tarif Lab whenever Klasifikasi, Kelas, or Jenis Changes ──
  const fetchTarifLab = async () => {
    if (!selectedKlasifikasi.kd || !selectedKelas.kd) return;
    setIsLoading(true);
    try {
      const res = await fetch(`${apiUrl}/show_tariflab`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kd_klasifikasi: selectedKlasifikasi.kd,
          kd_kelas: selectedKelas.kd,
          jenis: selectedJenis.nm || selectedJenis.kd || ''
        })
      });
      const data = await res.json();
      setTarifList(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetch /show_tariflab:', err);
      notify('danger', 'Gagal Memuat Tarif', `Terjadi kesalahan: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTarifLab();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKlasifikasi.kd, selectedKelas.kd, selectedJenis.nm, selectedJenis.kd]);

  // ── Auto Calculate Total Biaya ──
  useEffect(() => {
    const total = (parseFloat(editor.biayadasar) || 0) + (parseFloat(editor.alat) || 0) + (parseFloat(editor.bahan) || 0);
    setEditor(prev => ({ ...prev, biaya: total }));
  }, [editor.biayadasar, editor.alat, editor.bahan]);

  // ── Select Tindakan Lab from Master ──
  const handleSelectTindakan = (item) => {
    const code = item.kode || item.kd_tindakan || '';
    const name = item.nm_tindakan || item.nmtindakan || '';

    if (lookupTarget === 'RUJUKAN') {
      const existing = rujukanTarifList.find(p => p.KD_TINDAKAN === code);
      if (existing) {
        setRujukanInput({
          kd_tindakan: existing.KD_TINDAKAN,
          nm_tindakan: existing.NM_TINDAKAN,
          biaya: existing.BIAYA || 0,
          discrp: existing.DISCRP || 0,
          disc: existing.DISC || 0,
          harga: existing.HARGA || 0
        });
      } else {
        setRujukanInput(prev => ({
          ...prev,
          kd_tindakan: code,
          nm_tindakan: name
        }));
      }
      setShowLookupModal(false);
      return;
    }
    
    if (lookupTarget === 'PAKET') {
      const existing = paketTarifList.find(p => p.KD_TINDAKAN === code);
      if (existing) {
        setPaketInput({
          kd_tindakan: existing.KD_TINDAKAN,
          nm_tindakan: existing.NM_TINDAKAN,
          biayadasar: existing.BIAYADASAR || 0,
          alat: existing.ALAT || 0,
          bentuk: existing.BENTUK || '-',
          bahan: existing.BAHAN || 0,
          biaya: existing.BIAYA || 0
        });
      } else {
        setPaketInput(prev => ({
          ...prev,
          kd_tindakan: code,
          nm_tindakan: name
        }));
      }
      setShowLookupModal(false);
      return;
    }

    // Check if item already has a price in tarifList
    const existing = tarifList.find(t => t.KD_TINDAKAN === code);
    if (existing) {
      setEditor({
        kd_tindakan: existing.KD_TINDAKAN,
        nm_tindakan: existing.NM_TINDAKAN,
        biayadasar: existing.BIAYADASAR || 0,
        alat: existing.ALAT || 0,
        bentuk: existing.BENTUK || '-',
        bahan: existing.BAHAN || 0,
        biaya: existing.BIAYA || 0,
        isEdit: true
      });
    } else {
      setEditor(prev => ({
        ...prev,
        kd_tindakan: code,
        nm_tindakan: name,
        isEdit: false
      }));
    }
    setShowLookupModal(false);
  };

  // ── Fetch Lab Rujukan Master List ──
  useEffect(() => {
    const fetchLabRujukan = async () => {
      try {
        const res = await fetch(`${apiUrl}/show_labrujukan`);
        const json = await res.json();
        const list = json.list_lab || json.data || [];
        setLabRujukanList(list);
        if (list.length > 0) {
          setSelectedLabRujukan({ kd: list[0].kode, nm: list[0].nama });
        }
      } catch (err) {
        console.error('Error fetch show_labrujukan:', err);
      }
    };
    fetchLabRujukan();
  }, [apiUrl]);

  // ── Fetch Tarif Rujukan Lab ──
  const fetchTarifRujukanLab = useCallback(async () => {
    setIsRujukanLoading(true);
    try {
      const res = await fetch(`${apiUrl}/show_tarifrujukan_lab`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kdlab: selectedLabRujukan.kd || '',
          jenis: selectedRujukanJenis.kd || selectedRujukanJenis.nm || ''
        })
      });
      const data = await res.json();
      const list = data.list_harga || data.data || [];
      setRujukanTarifList(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Error fetch show_tarifrujukan_lab:', err);
      setRujukanTarifList([]);
    } finally {
      setIsRujukanLoading(false);
    }
  }, [apiUrl, selectedLabRujukan.kd, selectedRujukanJenis.kd, selectedRujukanJenis.nm]);

  useEffect(() => {
    if (activeTab === 'RUJUKAN') {
      fetchTarifRujukanLab();
    }
  }, [activeTab, fetchTarifRujukanLab]);

  // ── Add/Save Item to Rujukan Lab ──
  const handleAddRujukanItem = async () => {
    if (!selectedLabRujukan.kd) {
      notify('warning', 'Validasi Form', 'Harap pilih Laboratorium Rujukan terlebih dahulu!');
      return;
    }
    if (!rujukanInput.kd_tindakan && !rujukanInput.nm_tindakan) {
      notify('warning', 'Validasi Form', 'Harap pilih atau tentukan pelayanan laboratorium!');
      return;
    }

    try {
      const payload = {
        kdlab: selectedLabRujukan.kd,
        jenis: selectedRujukanJenis.kd || selectedRujukanJenis.nm || '',
        KD_TINDAKAN: rujukanInput.kd_tindakan,
        NM_TINDAKAN: rujukanInput.nm_tindakan,
        BIAYA: parseFloat(rujukanInput.biaya) || 0,
        DISCRP: parseFloat(rujukanInput.discrp) || 0,
        DISC: parseFloat(rujukanInput.disc) || 0,
        HARGA: parseFloat(rujukanInput.harga) || 0
      };

      const res = await fetch(`${apiUrl}/simpan_tarifrujukan_lab`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const json = await res.json();

      if (json.status === 1 || res.ok) {
        notify('success', 'Tarif Disimpan', `Tarif rujukan untuk ${rujukanInput.nm_tindakan || rujukanInput.kd_tindakan} berhasil disimpan.`);
        setRujukanInput({
          kd_tindakan: '',
          nm_tindakan: '',
          biaya: 0,
          discrp: 0,
          disc: 0,
          harga: 0
        });
        fetchTarifRujukanLab();
      } else {
        notify('danger', 'Gagal Simpan', json.msg || 'Gagal menyimpan tarif rujukan.');
      }
    } catch (err) {
      notify('danger', 'Kesalahan Sistem', err.message);
    }
  };

  // ── Delete Item from Rujukan Lab ──
  const handleDeleteRujukanItem = async (item) => {
    const code = item.KD_TINDAKAN || item.kd_tindakan;
    if (!window.confirm(`Hapus tarif rujukan "${item.NM_TINDAKAN || code}"?`)) return;

    try {
      const res = await fetch(`${apiUrl}/hapus_tarifrujukan_lab`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kdlab: item.KD_LAB || selectedLabRujukan.kd || '',
          jenis: item.kd_jenis || selectedRujukanJenis.kd || selectedRujukanJenis.nm || '',
          KD_TINDAKAN: code
        })
      });
      const json = await res.json();
      if (json.status === 1 || res.ok) {
        notify('success', 'Berhasil Dihapus', 'Tarif rujukan lab telah dihapus.');
        fetchTarifRujukanLab();
      } else {
        notify('danger', 'Gagal Hapus', json.msg || 'Gagal menghapus tarif rujukan.');
      }
    } catch (err) {
      notify('danger', 'Kesalahan Sistem', err.message);
    }
  };

  // ── Clear / Reset Rujukan Form ──
  const handleClearRujukan = () => {
    setRujukanInput({
      kd_tindakan: '',
      nm_tindakan: '',
      biaya: 0,
      discrp: 0,
      disc: 0,
      harga: 0
    });
  };

  // ── Fetch Tarif Paket Lab ──
  const fetchPaketLab = useCallback(async () => {
    setIsPaketLoading(true);
    try {
      const res = await fetch(`${apiUrl}/show_paketlab`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kd_klasifikasi: selectedKlasifikasi.kd || '',
          kd_kelas: selectedKelas.kd || '',
          jenis: selectedPaketJenis.nm || selectedJenis.nm || '',
          kd_paket: selectedPaket.kd || ''
        })
      });
      const data = await res.json();
      setPaketTarifList(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetch /show_paketlab:', err);
      setPaketTarifList([]);
    } finally {
      setIsPaketLoading(false);
    }
  }, [apiUrl, selectedKlasifikasi.kd, selectedKelas.kd, selectedPaketJenis.nm, selectedJenis.nm, selectedPaket.kd]);

  useEffect(() => {
    if (activeTab === 'PAKET') {
      fetchPaketLab();
    }
  }, [activeTab, fetchPaketLab]);

  // ── Add/Save Item to Paket Lab ──
  const handleAddPaketItem = async () => {
    if (!paketInput.kd_tindakan && !paketInput.nm_tindakan) {
      notify('warning', 'Validasi Paket', 'Silakan tentukan atau pilih pemeriksaan laboratorium!');
      return;
    }

    try {
      const payload = {
        kd_klasifikasi: selectedKlasifikasi.kd || '',
        kd_kelas: selectedKelas.kd || '',
        jenis: selectedPaketJenis.nm || selectedJenis.nm || '',
        kd_paket: selectedPaket.kd || '',
        KD_TINDAKAN: paketInput.kd_tindakan,
        NM_TINDAKAN: paketInput.nm_tindakan,
        BIAYADASAR: parseFloat(paketInput.biayadasar) || 0,
        ALAT: parseFloat(paketInput.alat) || 0,
        BENTUK: paketInput.bentuk || '-',
        BAHAN: parseFloat(paketInput.bahan) || 0,
        BIAYA: parseFloat(paketInput.biaya) || 0
      };

      const res = await fetch(`${apiUrl}/simpan_paketlab`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const json = await res.json();

      if (json.status === 1 || res.ok) {
        notify('success', 'Paket Disimpan', `Item ${paketInput.nm_tindakan || paketInput.kd_tindakan} berhasil disimpan.`);
        setPaketInput({
          kd_tindakan: '',
          nm_tindakan: '',
          biayadasar: 0,
          alat: 0,
          bentuk: '-',
          bahan: 0,
          biaya: 0
        });
        fetchPaketLab();
      } else {
        notify('danger', 'Gagal Simpan', json.msg || 'Gagal menyimpan item ke paket lab.');
      }
    } catch (err) {
      notify('danger', 'Kesalahan Sistem', err.message);
    }
  };

  // ── Delete Item from Paket Lab ──
  const handleDeletePaketItem = async (item) => {
    const code = item.KD_TINDAKAN || item.kd_tindakan;
    if (!window.confirm(`Hapus tarif pemeriksaan "${item.NM_TINDAKAN || code}" dari paket?`)) return;

    try {
      const res = await fetch(`${apiUrl}/hapus_paketlab`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kd_klasifikasi: item.klasifikasi || selectedKlasifikasi.kd || '',
          kd_kelas: item.klas || selectedKelas.kd || '',
          jenis: item.JENIS || selectedPaketJenis.nm || selectedJenis.nm || '',
          kd_paket: item.kd_paket || selectedPaket.kd || '',
          KD_TINDAKAN: code
        })
      });
      const json = await res.json();
      if (json.status === 1 || res.ok) {
        notify('success', 'Berhasil Dihapus', 'Item pemeriksaan berhasil dihapus dari paket.');
        fetchPaketLab();
      } else {
        notify('danger', 'Gagal Hapus', json.msg || 'Gagal menghapus item dari paket.');
      }
    } catch (err) {
      notify('danger', 'Kesalahan Sistem', err.message);
    }
  };

  // ── Batch Save Paket Lab ──
  const handleSaveAllPaket = async () => {
    if (paketInput.kd_tindakan) {
      await handleAddPaketItem();
    } else {
      notify('success', 'Tersimpan', 'Data rincian pelayanan laboratorium untuk paket telah tersimpan.');
      fetchPaketLab();
    }
  };

  // ── Save Current Editor Row to Server ──
  const handleSaveRow = async () => {
    if (!editor.kd_tindakan) {
      notify('warning', 'Validasi Form', 'Silakan pilih tindakan/pemeriksaan laboratorium terlebih dahulu!');
      return;
    }

    if (!selectedKlasifikasi.kd || !selectedKelas.kd) {
      notify('warning', 'Validasi Form', 'Klasifikasi dan Kelas tarif wajib dipilih!');
      return;
    }

    try {
      const payload = {
        kd_klasifikasi: selectedKlasifikasi.kd,
        kd_kelas: selectedKelas.kd,
        jenis: selectedJenis.nm || selectedJenis.kd || '',
        KD_TINDAKAN: editor.kd_tindakan,
        NM_TINDAKAN: editor.nm_tindakan,
        BIAYADASAR: parseFloat(editor.biayadasar) || 0,
        ALAT: parseFloat(editor.alat) || 0,
        BENTUK: editor.bentuk || '-',
        BAHAN: parseFloat(editor.bahan) || 0,
        BIAYA: parseFloat(editor.biaya) || 0
      };

      const res = await fetch(`${apiUrl}/simpan_tariflab`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const json = await res.json();

      if (json.status === 1 || res.ok) {
        notify('success', 'Tarif Disimpan', `Tarif untuk ${editor.nm_tindakan || editor.kd_tindakan} berhasil disimpan.`);
        // Reset editor
        setEditor({
          kd_tindakan: '',
          nm_tindakan: '',
          biayadasar: 0,
          alat: 0,
          bentuk: '-',
          bahan: 0,
          biaya: 0,
          isEdit: false
        });
        fetchTarifLab();
      } else {
        notify('danger', 'Gagal Menyimpan', json.msg || 'Gagal menyimpan tarif ke database.');
      }
    } catch (err) {
      console.error('Error simpan_tariflab:', err);
      notify('danger', 'Kesalahan Sistem', `Terjadi kendala: ${err.message}`);
    }
  };

  // ── Edit Row Click ──
  const handleEditRow = (item) => {
    setEditor({
      kd_tindakan: item.KD_TINDAKAN,
      nm_tindakan: item.NM_TINDAKAN,
      biayadasar: item.BIAYADASAR || 0,
      alat: item.ALAT || 0,
      bentuk: item.BENTUK || '-',
      bahan: item.BAHAN || 0,
      biaya: item.BIAYA || 0,
      isEdit: true
    });
    window.scrollTo({ top: 120, behavior: 'smooth' });
  };

  // ── Delete Row ──
  const handleDeleteRow = async (item) => {
    if (!window.confirm(`Yakin ingin menghapus tarif untuk pemeriksaan ${item.KD_TINDAKAN} - ${item.NM_TINDAKAN}?`)) return;

    try {
      const res = await fetch(`${apiUrl}/hapus_tariflab`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kd_klasifikasi: selectedKlasifikasi.kd,
          kd_kelas: selectedKelas.kd,
          jenis: item.JENIS || selectedJenis.nm || selectedJenis.kd || '',
          KD_TINDAKAN: item.KD_TINDAKAN
        })
      });
      const json = await res.json();
      if (json.status === 1 || res.ok) {
        notify('success', 'Berhasil Dihapus', `Tarif ${item.KD_TINDAKAN} telah dihapus.`);
        fetchTarifLab();
      } else {
        notify('danger', 'Gagal Menghapus', json.msg || 'Gagal menghapus tarif.');
      }
    } catch (err) {
      notify('danger', 'Kesalahan Sistem', `Gagal menghapus tarif: ${err.message}`);
    }
  };

  // ── Open Rincian Bahan Modal ──
  const handleOpenRincianBahan = async (item) => {
    setSelectedBahanItem(item);
    setShowRincianBahanModal(true);
    setIsRincianLoading(true);
    try {
      const res = await fetch(`${apiUrl}/show_rincianbahan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kd_tindakan: item.KD_TINDAKAN,
          kd_klasifikasi: item.klasifikasi || selectedKlasifikasi.kd,
          kd_kelas: item.klas || selectedKelas.kd
        })
      });
      const data = await res.json();
      setRincianBahanList(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching /show_rincianbahan:', err);
      notify('danger', 'Gagal Memuat Rincian Bahan', `Terjadi kendala: ${err.message}`);
    } finally {
      setIsRincianLoading(false);
    }
  };

  // ── Add/Save Bahan Item ──
  const handleAddBahanItem = async () => {
    if (!bahanInput.kd_obat && !bahanInput.nm_obat) {
      notify('warning', 'Validasi Bahan', 'Harap lengkapi kode atau nama barang!');
      return;
    }
    if (!selectedBahanItem) return;

    try {
      const payload = {
        kd_tindakan: selectedBahanItem.KD_TINDAKAN,
        kd_klasifikasi: selectedBahanItem.klasifikasi || selectedKlasifikasi.kd,
        kd_kelas: selectedBahanItem.klas || selectedKelas.kd,
        kd_obat: bahanInput.kd_obat || bahanInput.nm_obat,
        nm_obat: bahanInput.nm_obat || bahanInput.kd_obat,
        satuan: bahanInput.satuan || '-',
        hargadasar: parseFloat(bahanInput.hargadasar) || 0,
        margin: parseFloat(bahanInput.margin) || 0,
        qty: parseFloat(bahanInput.qty) || 1,
        harga: parseFloat(bahanInput.harga) || 0
      };

      const res = await fetch(`${apiUrl}/simpan_rincianbahan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const json = await res.json();
      if (json.status === 1 || res.ok) {
        notify('success', 'Bahan Disimpan', 'Item rincian bahan berhasil ditambahkan.');
        handleOpenRincianBahan(selectedBahanItem);
        fetchTarifLab();
        setBahanInput({
          kd_obat: '',
          nm_obat: '',
          satuan: '',
          hargadasar: 0,
          margin: 0,
          qty: 1,
          harga: 0
        });
      } else {
        notify('danger', 'Gagal Simpan Bahan', json.msg || 'Gagal menyimpan bahan.');
      }
    } catch (err) {
      notify('danger', 'Kesalahan Sistem', `Gagal: ${err.message}`);
    }
  };

  // ── Delete Bahan Item ──
  const handleDeleteBahanItem = async (b) => {
    if (!window.confirm(`Hapus komponen bahan ${b.nm_obat || b.kd_obat}?`)) return;
    try {
      const res = await fetch(`${apiUrl}/hapus_rincianbahan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kd_tindakan: selectedBahanItem.KD_TINDAKAN,
          kd_klasifikasi: selectedBahanItem.klasifikasi || selectedKlasifikasi.kd,
          kd_kelas: selectedBahanItem.klas || selectedKelas.kd,
          kd_obat: b.kd_obat
        })
      });
      const json = await res.json();
      if (json.status === 1 || res.ok) {
        notify('success', 'Bahan Dihapus', 'Komponen bahan berhasil dihapus.');
        handleOpenRincianBahan(selectedBahanItem);
        fetchTarifLab();
      }
    } catch (err) {
      notify('danger', 'Kesalahan Sistem', `Gagal hapus bahan: ${err.message}`);
    }
  };

  // ── Process/Close Bahan Modal ──
  const handleProcessBahan = async () => {
    if (bahanInput.kd_obat || bahanInput.nm_obat) {
      await handleAddBahanItem();
    }
    setShowRincianBahanModal(false);
    fetchTarifLab();
  };

  // ── Open Salin Modal ──
  const handleOpenSalinModal = () => {
    setSalinPayload({
      src_klasifikasi: selectedKlasifikasi.kd,
      src_kelas: selectedKelas.kd,
      src_jenis: selectedJenis.nm || '',
      dst_klasifikasi: '',
      dst_kelas: '',
      dst_jenis: selectedJenis.nm || '',
      mode_kenaikan: false,
      kenaikan_persen: 0
    });
    setSalinProgress(0);
    setShowSalinModal(true);
  };

  // ── Process Salin Tarif ──
  const handleProcessSalin = async () => {
    if (!salinPayload.src_klasifikasi || !salinPayload.src_kelas || !salinPayload.dst_klasifikasi || !salinPayload.dst_kelas) {
      notify('warning', 'Validasi Salin Tarif', 'Harap lengkapi klasifikasi dan kelas sumber serta tujuan!');
      return;
    }

    setIsSalinLoading(true);
    setSalinProgress(35);
    try {
      const progInterval = setInterval(() => {
        setSalinProgress(prev => (prev < 90 ? prev + 15 : prev));
      }, 150);

      const res = await fetch(`${apiUrl}/salin_tariflab`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(salinPayload)
      });
      clearInterval(progInterval);
      setSalinProgress(100);

      const json = await res.json();
      if (json.status === 1 || res.ok) {
        notify('success', 'Salin Tarif Berhasil', json.msg || 'Data tarif laboratorium berhasil disalin.');
        setTimeout(() => {
          setShowSalinModal(false);
          setSalinProgress(0);
          fetchTarifLab();
        }, 500);
      } else {
        notify('danger', 'Gagal Salin Tarif', json.msg || 'Gagal menyalin data tarif.');
      }
    } catch (err) {
      notify('danger', 'Kesalahan Sistem', `Gagal menyalin: ${err.message}`);
    } finally {
      setIsSalinLoading(false);
    }
  };

  // ── Print Function (In-App Preview & Progress Bar) ──
  const handlePrint = async (directDownload = false) => {
    const isRujukan = activeTab === 'RUJUKAN';
    const isPaket = activeTab === 'PAKET';

    let endpoint = '/cetak_tariflab';
    let queryParams = {};

    if (isRujukan) {
      endpoint = '/cetak_labrujukan';
      queryParams = {
        kdlab: selectedLabRujukan.kd || '',
        nmlab: selectedLabRujukan.nm || '',
        jenis: selectedRujukanJenis.kd || selectedRujukanJenis.nm || '',
        download: directDownload === true ? '1' : '0'
      };
    } else {
      endpoint = '/cetak_tariflab';
      queryParams = {
        klasifikasi: selectedKlasifikasi.kd || '',
        nm_klasifikasi: selectedKlasifikasi.nm || '',
        klas: selectedKelas.kd || '',
        nm_kelas: selectedKelas.nm || '',
        jenis: (isPaket ? selectedPaketJenis.nm : selectedJenis.nm) || '',
        download: directDownload === true ? '1' : '0'
      };
    }

    if (directDownload === true) {
      const params = new URLSearchParams(queryParams);
      window.location.href = `${apiUrl}${endpoint}?${params.toString()}`;
      return;
    }

    setPrintPdfUrl('');
    setShowPrintModal(true);
    setIsPdfLoading(true);
    setPdfProgress(10);

    const progInterval = setInterval(() => {
      setPdfProgress(prev => (prev < 90 ? prev + 15 : prev));
    }, 150);

    try {
      const params = new URLSearchParams(queryParams);
      const res = await fetch(`${apiUrl}${endpoint}?${params.toString()}`);
      if (!res.ok) throw new Error('Gagal memuat dokumen PDF');
      
      const blob = await res.blob();
      clearInterval(progInterval);
      setPdfProgress(100);

      const blobUrl = URL.createObjectURL(blob);
      setPrintPdfUrl(blobUrl);
      setTimeout(() => {
        setIsPdfLoading(false);
      }, 350);
    } catch (err) {
      clearInterval(progInterval);
      setIsPdfLoading(false);
      notify('danger', 'Gagal Memuat PDF', err.message);
    }
  };

  // ── Filtered & Paginated List ──
  const filteredTarifList = tarifList.filter(item => {
    if (!searchFilter) return true;
    const q = searchFilter.toLowerCase().trim();
    const kd = (item.KD_TINDAKAN || '').toLowerCase();
    const nm = (item.NM_TINDAKAN || '').toLowerCase();
    return kd.includes(q) || nm.includes(q);
  });

  const totalPages = Math.ceil(filteredTarifList.length / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedTarifList = filteredTarifList.slice(startIndex, startIndex + itemsPerPage);

  // Reset pagination on filter change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchFilter, selectedKlasifikasi.kd, selectedKelas.kd, selectedJenis.nm, selectedJenis.kd]);

  return (
    <div style={{
      minHeight: '100vh',
      background: '#e2e8f0',
      fontFamily: "'Segoe UI', 'Inter', sans-serif",
      display: 'flex',
      flexDirection: 'column',
      userSelect: 'none'
    }}>

      {/* ══ 1. WINDOW TITLE BAR (EXACT AS IMAGE) ══ */}
      <div style={{
        background: 'linear-gradient(180deg, #383838 0%, #222222 100%)',
        color: '#f8fafc',
        height: '32px',
        padding: '0 12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid #111',
        boxShadow: '0 2px 4px rgba(0,0,0,0.25)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ display: 'flex', gap: '5px' }}>
            <span style={{ width: 11, height: 11, borderRadius: '50%', background: '#ef4444', display: 'inline-block' }}></span>
            <span style={{ width: 11, height: 11, borderRadius: '50%', background: '#f59e0b', display: 'inline-block' }}></span>
            <span style={{ width: 11, height: 11, borderRadius: '50%', background: '#10b981', display: 'inline-block' }}></span>
          </div>
          <span style={{ fontSize: '0.78rem', fontWeight: '700', letterSpacing: '0.5px' }}>
            TABEL HARGA PELAYANAN LABORATORIUM
          </span>
        </div>
        <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
          <Button 
            variant="link" 
            size="sm" 
            className="text-white p-0 text-decoration-none" 
            onClick={() => navigate('/home')}
            title="Kembali ke Home"
          >
            <i className="bi bi-arrow-left me-1"></i>Kembali
          </Button>
        </div>
      </div>

      {/* ══ NAVIGATION TABS ══ */}
      <div style={{
        background: '#1e293b',
        borderBottom: '2px solid #0f172a',
        padding: '6px 14px 0 14px',
        display: 'flex',
        gap: '4px'
      }}>
        <button
          type="button"
          style={{
            background: activeTab === 'REGULER' ? '#f1f5f9' : '#334155',
            color: activeTab === 'REGULER' ? '#0f172a' : '#cbd5e1',
            fontWeight: '800',
            fontSize: '0.78rem',
            padding: '7px 18px',
            border: 'none',
            borderTopLeftRadius: '6px',
            borderTopRightRadius: '6px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            borderBottom: activeTab === 'REGULER' ? '3px solid #0284c7' : 'none'
          }}
          onClick={() => setActiveTab('REGULER')}
        >
          <i className="bi bi-card-checklist text-primary"></i>
          TARIF LABORATORIUM (REGULER)
        </button>

        <button
          type="button"
          style={{
            background: activeTab === 'PAKET' ? '#f1f5f9' : '#334155',
            color: activeTab === 'PAKET' ? '#0f172a' : '#cbd5e1',
            fontWeight: '800',
            fontSize: '0.78rem',
            padding: '7px 18px',
            border: 'none',
            borderTopLeftRadius: '6px',
            borderTopRightRadius: '6px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            borderBottom: activeTab === 'PAKET' ? '3px solid #0284c7' : 'none'
          }}
          onClick={() => setActiveTab('PAKET')}
        >
          <i className="bi bi-box-seam-fill text-warning"></i>
          TARIF PAKET LABORATORIUM
        </button>

        <button
          type="button"
          style={{
            background: activeTab === 'RUJUKAN' ? '#f1f5f9' : '#334155',
            color: activeTab === 'RUJUKAN' ? '#0f172a' : '#cbd5e1',
            fontWeight: '800',
            fontSize: '0.78rem',
            padding: '7px 18px',
            border: 'none',
            borderTopLeftRadius: '6px',
            borderTopRightRadius: '6px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            borderBottom: activeTab === 'RUJUKAN' ? '3px solid #0284c7' : 'none'
          }}
          onClick={() => setActiveTab('RUJUKAN')}
        >
          <i className="bi bi-hospital text-info"></i>
          TARIF LABORATORIUM RUJUKAN
        </button>
      </div>

      {activeTab === 'REGULER' && (
        <>
          {/* ══ 2. SUB-HEADER BANNER ══ */}
          <div style={{
            background: T.blueDark,
            color: '#ffffff',
            textAlign: 'center',
            padding: '6px 0',
            fontWeight: '800',
            fontSize: '0.86rem',
            letterSpacing: '1px',
            borderBottom: '2px solid #0284c7'
          }}>
            TARIF PELAYANAN LABORATORIUM BERLAKU
          </div>

      {/* ══ 3. MAIN CONTAINER ══ */}
      <div style={{ padding: '10px 14px', flex: 1, display: 'flex', flexDirection: 'column', gap: '10px' }}>

        {/* ══ FORM HEADER INPUTS (KLASIFIKASI, KELAS, JENIS PEMERIKSAAN) ══ */}
        <div style={{
          background: '#d1d5db',
          border: '1px solid #9ca3af',
          borderRadius: '4px',
          padding: '10px 14px',
          display: 'flex',
          justifyContent: 'center'
        }}>
          <div style={{ width: '100%', maxWidth: '620px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
            
            {/* ROW 1: KLASIFIKASI TARIF */}
            <div style={{ display: 'flex', alignItems: 'stretch', height: '28px' }}>
              <div style={{
                background: T.blueHdr,
                color: '#fff',
                fontSize: '0.75rem',
                fontWeight: '700',
                padding: '0 10px',
                width: '180px',
                display: 'flex',
                alignItems: 'center',
                border: '1px solid #003666',
                borderRight: 'none'
              }}>
                KLASIFIKASI TARIF
              </div>
              <input
                type="text"
                style={{
                  width: '55px',
                  background: '#fef08a',
                  border: '1px solid #9ca3af',
                  textAlign: 'center',
                  fontSize: '0.78rem',
                  fontWeight: '700',
                  outline: 'none'
                }}
                value={selectedKlasifikasi.kd}
                onChange={e => {
                  const val = e.target.value;
                  const match = klasifikasiOpts.find(k => (k.kd_klas || k.kd_klasifikasi || k.kode) === val);
                  setSelectedKlasifikasi({
                    kd: val,
                    nm: match ? (match.nm_klas || match.nm_klasifikasi || match.nama) : selectedKlasifikasi.nm
                  });
                }}
              />
              <select
                style={{
                  flex: 1,
                  background: '#fff',
                  border: '1px solid #9ca3af',
                  borderLeft: 'none',
                  fontSize: '0.78rem',
                  fontWeight: '600',
                  padding: '0 8px',
                  outline: 'none'
                }}
                value={selectedKlasifikasi.kd}
                onChange={e => {
                  const val = e.target.value;
                  const match = klasifikasiOpts.find(k => (k.kd_klas || k.kd_klasifikasi || k.kode) === val);
                  setSelectedKlasifikasi({
                    kd: val,
                    nm: match ? (match.nm_klas || match.nm_klasifikasi || match.nama) : ''
                  });
                }}
              >
                <option value="">-- Pilih Klasifikasi --</option>
                {klasifikasiOpts.map((k, i) => {
                  const kd = k.kd_klas || k.kd_klasifikasi || k.kode;
                  const nm = k.nm_klas || k.nm_klasifikasi || k.nama;
                  return (
                    <option key={i} value={kd}>
                      {kd} - {nm}
                    </option>
                  );
                })}
              </select>
            </div>

            {/* ROW 2: KELAS TARIF */}
            <div style={{ display: 'flex', alignItems: 'stretch', height: '28px' }}>
              <div style={{
                background: T.blueHdr,
                color: '#fff',
                fontSize: '0.75rem',
                fontWeight: '700',
                padding: '0 10px',
                width: '180px',
                display: 'flex',
                alignItems: 'center',
                border: '1px solid #003666',
                borderRight: 'none'
              }}>
                KELAS TARIF
              </div>
              <input
                type="text"
                style={{
                  width: '55px',
                  background: '#fff',
                  border: '1px solid #9ca3af',
                  textAlign: 'center',
                  fontSize: '0.78rem',
                  fontWeight: '700',
                  outline: 'none'
                }}
                value={selectedKelas.kd}
                onChange={e => {
                  const val = e.target.value;
                  const match = kelasOpts.find(k => (k.kd_klas || k.kd_kelas || k.kode) === val);
                  setSelectedKelas({
                    kd: val,
                    nm: match ? (match.nm_klas || match.nm_kelas || match.nama) : selectedKelas.nm
                  });
                }}
              />
              <select
                style={{
                  flex: 1,
                  background: '#fff',
                  border: '1px solid #9ca3af',
                  borderLeft: 'none',
                  fontSize: '0.78rem',
                  fontWeight: '600',
                  padding: '0 8px',
                  outline: 'none'
                }}
                value={selectedKelas.kd}
                onChange={e => {
                  const val = e.target.value;
                  const match = kelasOpts.find(k => (k.kd_klas || k.kd_kelas || k.kode) === val);
                  setSelectedKelas({
                    kd: val,
                    nm: match ? (match.nm_klas || match.nm_kelas || match.nama) : ''
                  });
                }}
              >
                <option value="">-- Pilih Kelas --</option>
                {kelasOpts.map((k, i) => {
                  const kd = k.kd_klas || k.kd_kelas || k.kode;
                  const nm = k.nm_klas || k.nm_kelas || k.nama;
                  return (
                    <option key={i} value={kd}>
                      {kd} - {nm}
                    </option>
                  );
                })}
              </select>
            </div>

            {/* ROW 3: JENIS PEMERIKSAAN */}
            <div style={{ display: 'flex', alignItems: 'stretch', height: '28px' }}>
              <div style={{
                background: T.blueHdr,
                color: '#fff',
                fontSize: '0.75rem',
                fontWeight: '700',
                padding: '0 10px',
                width: '180px',
                display: 'flex',
                alignItems: 'center',
                border: '1px solid #003666',
                borderRight: 'none'
              }}>
                JENIS PEMERIKSAAN
              </div>
              <input
                type="text"
                style={{
                  width: '55px',
                  background: '#fff',
                  border: '1px solid #9ca3af',
                  textAlign: 'center',
                  fontSize: '0.78rem',
                  fontWeight: '700',
                  outline: 'none'
                }}
                value={selectedJenis.kd}
                onChange={e => {
                  const val = e.target.value;
                  const match = jenisLabOpts.find(j => (j.kode || j.jenis) === val);
                  setSelectedJenis({ kd: val, nm: match ? (match.jenis || match.nm_jenis) : selectedJenis.nm });
                }}
              />
              <select
                style={{
                  flex: 1,
                  background: '#fff',
                  border: '1px solid #9ca3af',
                  borderLeft: 'none',
                  fontSize: '0.78rem',
                  fontWeight: '600',
                  padding: '0 8px',
                  outline: 'none'
                }}
                value={selectedJenis.nm}
                onChange={e => {
                  const val = e.target.value;
                  const match = jenisLabOpts.find(j => (j.jenis || j.nm_jenis) === val);
                  setSelectedJenis({ kd: match ? (match.kode || '') : '', nm: val });
                }}
              >
                <option value="">-- Semua Jenis Pemeriksaan --</option>
                {jenisLabOpts.map((j, i) => (
                  <option key={i} value={j.jenis || j.nm_jenis}>
                    {j.kode ? `${j.kode} - ` : ''}{j.jenis || j.nm_jenis}
                  </option>
                ))}
              </select>
            </div>

          </div>
        </div>

        {/* ══ TEAL TABLE HEADER & INPUT ENTRY BAR (EXACT AS IMAGE) ══ */}
        <div style={{
          background: '#fff',
          border: '1px solid #94a3b8',
          borderRadius: '4px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
        }}>
          
          {/* TEAL TABLE HEADER ROW */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(280px, 2.5fr) 110px 110px 190px 120px 80px',
            background: T.tealHdr,
            color: '#fff',
            fontWeight: '700',
            fontSize: '0.74rem',
            textAlign: 'center',
            borderBottom: '1px solid rgba(0,0,0,0.2)'
          }}>
            <div style={{ padding: '8px 6px', borderRight: '1px solid rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              NAMA PEMERIKSA LABORATORIUM
            </div>
            <div style={{ padding: '8px 6px', borderRight: '1px solid rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              JASA [Rp]
            </div>
            <div style={{ padding: '8px 6px', borderRight: '1px solid rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              SEWA ALAT [Rp]
            </div>
            <div style={{ borderRight: '1px solid rgba(255,255,255,0.2)', display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: '3px 0', borderBottom: '1px solid rgba(255,255,255,0.2)', background: T.tealSub }}>
                NILAI BAHAN
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                <div style={{ padding: '4px 0', borderRight: '1px solid rgba(255,255,255,0.2)', fontSize: '0.69rem' }}>BENTUK</div>
                <div style={{ padding: '4px 0', fontSize: '0.69rem' }}>[Rp]</div>
              </div>
            </div>
            <div style={{ padding: '8px 6px', borderRight: '1px solid rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              TOTAL TARIF [Rp]
            </div>
            <div style={{ padding: '8px 6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              AKSI
            </div>
          </div>

          {/* INPUT FORM ROW (TEAL ROW AS IMAGE) */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(280px, 2.5fr) 110px 110px 190px 120px 80px',
            background: T.tealRow,
            padding: '4px',
            gap: '3px',
            borderBottom: '2px solid #cbd5e1',
            alignItems: 'center'
          }}>
            {/* ITEM PEMERIKSAAN INPUT */}
            <div style={{ display: 'flex', gap: '3px', alignItems: 'stretch' }}>
              <input
                ref={kdInputRef}
                type="text"
                placeholder="Kode"
                style={{
                  width: '65px',
                  height: '28px',
                  background: '#fff',
                  border: '1px solid #94a3b8',
                  fontSize: '0.75rem',
                  fontWeight: '700',
                  textAlign: 'center',
                  borderRadius: '3px',
                  outline: 'none'
                }}
                value={editor.kd_tindakan}
                onChange={e => {
                  const val = e.target.value;
                  const match = tindakanLabList.find(t => (t.kode || t.kd_tindakan) === val);
                  setEditor(prev => ({
                    ...prev,
                    kd_tindakan: val,
                    nm_tindakan: match ? (match.nm_tindakan || match.nmtindakan) : prev.nm_tindakan
                  }));
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    if (!editor.nm_tindakan) setShowLookupModal(true);
                  }
                }}
              />
              <div style={{ flex: 1, display: 'flex', gap: '2px' }}>
                <input
                  type="text"
                  placeholder="Klik untuk cari nama tindakan lab..."
                  readOnly
                  style={{
                    flex: 1,
                    height: '28px',
                    background: '#fff',
                    border: '1px solid #94a3b8',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    padding: '0 8px',
                    borderRadius: '3px',
                    cursor: 'pointer'
                  }}
                  value={editor.nm_tindakan}
                  onClick={() => setShowLookupModal(true)}
                />
                <Button 
                  variant="outline-primary" 
                  size="sm" 
                  style={{ height: '28px', padding: '0 8px', display: 'flex', alignItems: 'center' }}
                  onClick={() => setShowLookupModal(true)}
                  title="Cari Master Tindakan Lab"
                >
                  <i className="bi bi-search"></i>
                </Button>
              </div>
            </div>

            {/* JASA [Rp] */}
            <div>
              <input
                type="number"
                style={{
                  width: '100%',
                  height: '28px',
                  background: '#fff',
                  border: '1px solid #94a3b8',
                  fontSize: '0.75rem',
                  fontWeight: '700',
                  textAlign: 'right',
                  padding: '0 6px',
                  borderRadius: '3px',
                  outline: 'none'
                }}
                value={editor.biayadasar}
                onChange={e => setEditor({ ...editor, biayadasar: e.target.value })}
                onKeyDown={e => e.key === 'Enter' && handleSaveRow()}
              />
            </div>

            {/* SEWA ALAT [Rp] */}
            <div>
              <input
                type="number"
                style={{
                  width: '100%',
                  height: '28px',
                  background: '#fff',
                  border: '1px solid #94a3b8',
                  fontSize: '0.75rem',
                  fontWeight: '700',
                  textAlign: 'right',
                  padding: '0 6px',
                  borderRadius: '3px',
                  outline: 'none'
                }}
                value={editor.alat}
                onChange={e => setEditor({ ...editor, alat: e.target.value })}
                onKeyDown={e => e.key === 'Enter' && handleSaveRow()}
              />
            </div>

            {/* NILAI BAHAN: BENTUK & [Rp] */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px' }}>
              <select
                style={{
                  height: '28px',
                  background: '#fff',
                  border: '1px solid #94a3b8',
                  fontSize: '0.72rem',
                  fontWeight: '600',
                  padding: '0 4px',
                  borderRadius: '3px',
                  outline: 'none'
                }}
                value={editor.bentuk}
                onChange={e => setEditor({ ...editor, bentuk: e.target.value })}
              >
                <option value="-">-</option>
                <option value="TEST">TEST</option>
                <option value="VIAL">VIAL</option>
                <option value="AMPULE">AMPULE</option>
                <option value="STRIP">STRIP</option>
                <option value="PCS">PCS</option>
                <option value="BOTOL">BOTOL</option>
                <option value="KIT">KIT</option>
                <option value="TABUNG">TABUNG</option>
                <option value="PACK">PACK</option>
              </select>
              <input
                type="number"
                style={{
                  width: '100%',
                  height: '28px',
                  background: '#fff',
                  border: '1px solid #94a3b8',
                  fontSize: '0.75rem',
                  fontWeight: '700',
                  textAlign: 'right',
                  padding: '0 6px',
                  borderRadius: '3px',
                  outline: 'none'
                }}
                value={editor.bahan}
                onChange={e => setEditor({ ...editor, bahan: e.target.value })}
                onKeyDown={e => e.key === 'Enter' && handleSaveRow()}
              />
            </div>

            {/* TOTAL TARIF [Rp] */}
            <div>
              <input
                type="text"
                readOnly
                style={{
                  width: '100%',
                  height: '28px',
                  background: '#f1f5f9',
                  border: '1px solid #94a3b8',
                  fontSize: '0.78rem',
                  fontWeight: '800',
                  textAlign: 'right',
                  padding: '0 6px',
                  borderRadius: '3px',
                  color: '#0369a1'
                }}
                value={fmt(editor.biaya)}
              />
            </div>

            {/* ACTION BUTTON ADD / UPDATE */}
            <div>
              <Button
                variant={editor.isEdit ? "warning" : "success"}
                size="sm"
                style={{
                  width: '100%',
                  height: '28px',
                  fontSize: '0.72rem',
                  fontWeight: '700',
                  padding: '0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px'
                }}
                onClick={handleSaveRow}
                title={editor.isEdit ? "Simpan Perubahan Baris" : "Tambah Item Tarif"}
              >
                <i className={`bi ${editor.isEdit ? 'bi-check2' : 'bi-plus-lg'}`}></i>
                {editor.isEdit ? 'Update' : 'Tambah'}
              </Button>
            </div>

          </div>

          {/* SEARCH & TOTAL SUMMARY BAR */}
          <div style={{
            background: '#f8fafc',
            padding: '6px 10px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #e2e8f0'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <InputGroup size="sm" style={{ width: '260px' }}>
                <InputGroup.Text style={{ background: '#fff', fontSize: '0.72rem' }}><i className="bi bi-search"></i></InputGroup.Text>
                <FormControl
                  placeholder="Filter nama / kode pemeriksaan..."
                  value={searchFilter}
                  onChange={e => setSearchFilter(e.target.value)}
                  style={{ fontSize: '0.75rem' }}
                />
                {searchFilter && (
                  <Button variant="outline-secondary" size="sm" onClick={() => setSearchFilter('')}><i className="bi bi-x"></i></Button>
                )}
              </InputGroup>
            </div>
            <div style={{ fontSize: '0.75rem', color: '#475569', fontWeight: '600' }}>
              Jumlah Tarif Terdaftar: <Badge bg="primary" className="font-monospace ms-1">{filteredTarifList.length}</Badge> item
            </div>
          </div>

          {/* ══ MAIN DATA TABLE ══ */}
          <div style={{ flex: 1, minHeight: '380px', maxHeight: '480px', overflowY: 'auto' }}>
            <Table hover striped bordered size="sm" className="mb-0 align-middle" style={{ fontSize: '0.75rem' }}>
              <thead style={{ background: '#0f172a', color: '#fff', position: 'sticky', top: 0, zIndex: 2 }}>
                <tr>
                  <th className="text-center" style={{ width: '40px' }}>No</th>
                  <th className="text-center" style={{ width: '90px' }}>Kode</th>
                  <th>Nama Pemeriksaan Laboratorium</th>
                  <th className="text-center" style={{ width: '130px' }}>Jenis</th>
                  <th className="text-end" style={{ width: '110px' }}>Jasa [Rp]</th>
                  <th className="text-end" style={{ width: '110px' }}>Sewa Alat [Rp]</th>
                  <th className="text-center" style={{ width: '80px' }}>Bentuk</th>
                  <th className="text-end" style={{ width: '110px' }}>Nilai Bahan [Rp]</th>
                  <th className="text-end" style={{ width: '120px' }}>Total Tarif [Rp]</th>
                  <th className="text-center" style={{ width: '85px' }}>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan="10" className="text-center py-5 text-muted">
                      <Spinner animation="border" variant="teal" size="sm" className="me-2" />
                      Memuat daftar tarif laboratorium...
                    </td>
                  </tr>
                ) : paginatedTarifList.length > 0 ? (
                  paginatedTarifList.map((row, idx) => (
                    <tr 
                      key={idx} 
                      style={{ cursor: 'pointer', background: editor.kd_tindakan === row.KD_TINDAKAN ? '#fef3c7' : undefined }}
                      onDoubleClick={() => handleEditRow(row)}
                    >
                      <td className="text-center text-muted">{startIndex + idx + 1}</td>
                      <td className="text-center font-monospace fw-bold text-primary">{row.KD_TINDAKAN}</td>
                      <td className="fw-semibold text-dark">{row.NM_TINDAKAN}</td>
                      <td className="text-center text-muted" style={{ fontSize: '0.72rem' }}>{row.JENIS || '-'}</td>
                      <td className="text-end font-monospace">{fmt(row.BIAYADASAR)}</td>
                      <td className="text-end font-monospace">{fmt(row.ALAT)}</td>
                      <td className="text-center font-monospace text-muted">{row.BENTUK || '-'}</td>
                      <td className="text-end font-monospace">
                        {parseFloat(row.BAHAN) > 0 ? (
                          <span
                            role="button"
                            className="text-primary fw-bold text-decoration-underline"
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenRincianBahan(row);
                            }}
                            title="Klik untuk melihat rincian bahan"
                          >
                            {fmt(row.BAHAN)}
                          </span>
                        ) : (
                          <span className="text-muted">{fmt(row.BAHAN)}</span>
                        )}
                      </td>
                      <td className="text-end font-monospace fw-bold text-success" style={{ fontSize: '0.8rem' }}>
                        {fmt(row.BIAYA)}
                      </td>
                      <td className="text-center">
                        <div className="d-flex justify-content-center gap-1">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            style={{ padding: '1px 6px', fontSize: '0.68rem' }}
                            onClick={() => handleEditRow(row)}
                            title="Edit Tarif"
                          >
                            <i className="bi bi-pencil-square"></i>
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            style={{ padding: '1px 6px', fontSize: '0.68rem' }}
                            onClick={() => handleDeleteRow(row)}
                            title="Hapus Tarif"
                          >
                            <i className="bi bi-trash"></i>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="10" className="text-center py-5 text-muted">
                      <i className="bi bi-folder2-open d-block fs-3 opacity-25 mb-2"></i>
                      Tidak ada tarif laboratorium untuk Klasifikasi <strong>{selectedKlasifikasi.nm}</strong> & Kelas <strong>{selectedKelas.nm}</strong>.
                      <div className="mt-2 text-muted" style={{ fontSize: '0.72rem' }}>
                        Gunakan form di atas untuk menambah tarif baru atau klik tombol <strong>SALIN</strong> untuk menduplikasi tarif dari kelas/klasifikasi lain.
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          {/* ══ PAGINATION BAR ══ */}
          <div style={{
            background: '#f8fafc',
            borderTop: '1px solid #cbd5e1',
            padding: '6px 12px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '8px'
          }}>
            {/* Left: Rows Per Page & Counter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.75rem', color: '#475569' }}>
              <div className="d-flex align-items-center gap-1">
                <span>Tampilkan:</span>
                <Form.Select
                  size="sm"
                  style={{ width: '65px', height: '26px', fontSize: '0.72rem', padding: '0 4px', fontWeight: '600' }}
                  value={itemsPerPage}
                  onChange={e => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </Form.Select>
                <span>baris</span>
              </div>
              <span className="text-muted">|</span>
              <div>
                Menampilkan <strong>{filteredTarifList.length > 0 ? startIndex + 1 : 0}</strong> - <strong>{Math.min(startIndex + itemsPerPage, filteredTarifList.length)}</strong> dari <strong>{filteredTarifList.length}</strong> data
              </div>
            </div>

            {/* Right: Pagination Controls */}
            <div className="d-flex align-items-center gap-1">
              <Button
                variant="outline-secondary"
                size="sm"
                style={{ height: '26px', padding: '0 8px', fontSize: '0.72rem' }}
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(1)}
                title="Halaman Pertama"
              >
                <i className="bi bi-chevron-double-left"></i>
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                style={{ height: '26px', padding: '0 8px', fontSize: '0.72rem' }}
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                title="Halaman Sebelumnya"
              >
                <i className="bi bi-chevron-left"></i>
              </Button>

              <div style={{ fontSize: '0.75rem', fontWeight: '700', padding: '0 8px', color: '#1e293b' }}>
                Halaman {currentPage} / {totalPages}
              </div>

              <Button
                variant="outline-secondary"
                size="sm"
                style={{ height: '26px', padding: '0 8px', fontSize: '0.72rem' }}
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                title="Halaman Selanjutnya"
              >
                <i className="bi bi-chevron-right"></i>
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                style={{ height: '26px', padding: '0 8px', fontSize: '0.72rem' }}
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage(totalPages)}
                title="Halaman Terakhir"
              >
                <i className="bi bi-chevron-double-right"></i>
              </Button>
            </div>
          </div>

        </div>

      </div>

      {/* ══ 4. BOTTOM ACTION BAR (EXACT AS IMAGE) ══ */}
      <div style={{
        background: '#d1d5db',
        borderTop: '2px solid #9ca3af',
        padding: '6px 16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        {/* LEFT EXIT HINT */}
        <div style={{ color: '#991b1b', fontWeight: '800', fontSize: '0.78rem', letterSpacing: '0.5px' }}>
          Tombol [Esc] Untuk Keluar
        </div>

        {/* RIGHT ACTION BUTTONS: SALIN, CETAK, SIMPAN */}
        <div style={{ display: 'flex', gap: '8px' }}>
          
          {/* SALIN */}
          <button
            style={{
              background: '#f8fafc',
              border: '1px solid #94a3b8',
              borderRadius: '4px',
              padding: '4px 14px',
              fontSize: '0.76rem',
              fontWeight: '700',
              color: '#1e293b',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              cursor: 'pointer',
              boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
            }}
            onClick={handleOpenSalinModal}
          >
            <i className="bi bi-files text-primary"></i>
            SALIN
          </button>

          {/* CETAK */}
          <button
            style={{
              background: '#f8fafc',
              border: '1px solid #94a3b8',
              borderRadius: '4px',
              padding: '4px 14px',
              fontSize: '0.76rem',
              fontWeight: '700',
              color: '#1e293b',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              cursor: 'pointer',
              boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
            }}
            onClick={() => handlePrint(false)}
          >
            <i className="bi bi-printer text-dark"></i>
            CETAK
          </button>

          {/* SIMPAN */}
          <button
            style={{
              background: '#f8fafc',
              border: '1px solid #94a3b8',
              borderRadius: '4px',
              padding: '4px 14px',
              fontSize: '0.76rem',
              fontWeight: '700',
              color: '#1e293b',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              cursor: 'pointer',
              boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
            }}
            onClick={handleSaveRow}
          >
            <i className="bi bi-floppy text-success"></i>
            SIMPAN
          </button>

        </div>
      </div>
    </>
  )}

  {/* ═══════════════════════════════════════════════════════════ */}
  {/* ══ TAB 2: TARIF PAKET LABORATORIUM (media_1788251610583) ══ */}
  {/* ═══════════════════════════════════════════════════════════ */}
  {activeTab === 'PAKET' && (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      
      {/* TOP TITLE BANNER */}
      <div style={{
        background: '#002b49',
        color: '#ffffff',
        textAlign: 'center',
        padding: '6px 0',
        fontWeight: '800',
        fontSize: '0.86rem',
        letterSpacing: '1px',
        borderBottom: '2px solid #0284c7'
      }}>
        RINCIAN PELAYANAN LABORATORIUM UNTUK PAKET
      </div>

      <div style={{ padding: '10px 14px', flex: 1, display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {/* TOP SELECTION: JENIS */}
        <div style={{
          background: '#d1d5db',
          border: '1px solid #9ca3af',
          borderRadius: '4px',
          padding: '8px 14px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0px' }}>
            <span style={{
              background: '#004b87',
              color: '#ffffff',
              fontWeight: '800',
              fontSize: '0.74rem',
              padding: '4px 14px',
              borderRadius: '3px 0 0 3px',
              display: 'inline-block'
            }}>
              JENIS
            </span>
            <input
              type="text"
              readOnly
              value={selectedPaketJenis.kd}
              style={{
                width: '50px',
                background: '#ffffff',
                border: '1px solid #94a3b8',
                borderLeft: 'none',
                fontSize: '0.74rem',
                fontWeight: '700',
                textAlign: 'center',
                padding: '3px 0',
                outline: 'none'
              }}
            />
            <select
              style={{
                width: '280px',
                background: '#ffffff',
                border: '1px solid #94a3b8',
                borderLeft: 'none',
                borderRadius: '0 3px 3px 0',
                fontSize: '0.74rem',
                fontWeight: '600',
                padding: '3px 8px',
                color: '#0f172a',
                outline: 'none'
              }}
              value={selectedPaketJenis.kd}
              onChange={(e) => {
                const val = e.target.value;
                const found = jenisLabOpts.find(j => (j.kode || j.jenis || j.kd_jenis) === val);
                if (found) {
                  setSelectedPaketJenis({ kd: found.kode || found.kd_jenis || val, nm: found.jenis || found.nm_jenis || val });
                } else {
                  setSelectedPaketJenis({ kd: '', nm: '' });
                }
              }}
            >
              <option value="">-- SEMUA JENIS PEMERIKSAAN --</option>
              {jenisLabOpts.map((j, idx) => {
                const code = j.kode || j.kd_jenis || j.jenis || idx;
                const name = j.jenis || j.nm_jenis || j.nm;
                return <option key={code} value={code}>{name}</option>;
              })}
            </select>
          </div>
        </div>

        {/* MAIN TABLE CONTAINER */}
        <div style={{
          background: '#ffffff',
          border: '1px solid #94a3b8',
          borderRadius: '4px',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 2px 4px rgba(0,0,0,0.06)',
          overflow: 'hidden',
          flex: 1
        }}>
          <div style={{ overflowX: 'auto', flex: 1, maxHeight: 'calc(100vh - 290px)', minHeight: '320px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.74rem' }}>
              {/* TEAL HEADER */}
              <thead>
                <tr style={{ background: '#007580', color: '#ffffff', textAlign: 'center', fontWeight: '800' }}>
                  <th rowSpan={2} style={{ padding: '6px 8px', borderRight: '1px solid #005f68', width: '42%' }}>
                    NAMA PEMERIKSA LABORATORIUM
                  </th>
                  <th rowSpan={2} style={{ padding: '6px 8px', borderRight: '1px solid #005f68', width: '13%' }}>
                    JASA [Rp]
                  </th>
                  <th rowSpan={2} style={{ padding: '6px 8px', borderRight: '1px solid #005f68', width: '13%' }}>
                    SEWA ALAT [Rp]
                  </th>
                  <th colSpan={2} style={{ padding: '4px 8px', borderRight: '1px solid #005f68', width: '18%', borderBottom: '1px solid #005f68' }}>
                    NILAI BAHAN
                  </th>
                  <th rowSpan={2} style={{ padding: '6px 8px', borderRight: '1px solid #005f68', width: '14%' }}>
                    TOTAL TARIF [Rp]
                  </th>
                  <th rowSpan={2} style={{ padding: '6px 8px', width: '50px' }}>
                    AKSI
                  </th>
                </tr>
                <tr style={{ background: '#007580', color: '#ffffff', textAlign: 'center', fontWeight: '800' }}>
                  <th style={{ padding: '3px 6px', borderRight: '1px solid #005f68', width: '9%' }}>
                    BENTUK
                  </th>
                  <th style={{ padding: '3px 6px', borderRight: '1px solid #005f68', width: '9%' }}>
                    [Rp]
                  </th>
                </tr>

                {/* SUB-INPUT ROW (EXACT AS IMAGE) */}
                <tr style={{ background: '#f1f5f9', borderBottom: '2px solid #cbd5e1' }}>
                  {/* NAMA PEMERIKSA INPUT */}
                  <td style={{ padding: '4px', borderRight: '1px solid #cbd5e1' }}>
                    <div style={{ display: 'flex', gap: '3px' }}>
                      <input
                        type="text"
                        placeholder="Kode"
                        value={paketInput.kd_tindakan}
                        onChange={(e) => setPaketInput({ ...paketInput, kd_tindakan: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === 'F1' || e.key === 'Enter') {
                            setLookupTarget('PAKET');
                            setLookupSearch('');
                            setShowLookupModal(true);
                          }
                        }}
                        style={{
                          width: '75px',
                          border: '1px solid #94a3b8',
                          borderRadius: '2px',
                          padding: '2px 4px',
                          fontSize: '0.72rem',
                          fontWeight: '700',
                          textAlign: 'center'
                        }}
                      />
                      <input
                        type="text"
                        placeholder="Nama Pemeriksaan (Klik / F1 untuk Lookup)"
                        value={paketInput.nm_tindakan}
                        readOnly
                        onClick={() => {
                          setLookupTarget('PAKET');
                          setLookupSearch('');
                          setShowLookupModal(true);
                        }}
                        style={{
                          flex: 1,
                          border: '1px solid #94a3b8',
                          borderRadius: '2px',
                          padding: '2px 6px',
                          fontSize: '0.72rem',
                          fontWeight: '600',
                          background: '#fff',
                          cursor: 'pointer'
                        }}
                      />
                      <button
                        type="button"
                        style={{
                          border: '1px solid #94a3b8',
                          background: '#e2e8f0',
                          padding: '2px 6px',
                          borderRadius: '2px',
                          cursor: 'pointer'
                        }}
                        onClick={() => {
                          setLookupTarget('PAKET');
                          setLookupSearch('');
                          setShowLookupModal(true);
                        }}
                        title="Buka Pencarian Tindakan (F1)"
                      >
                        <i className="bi bi-search"></i>
                      </button>
                    </div>
                  </td>

                  {/* JASA */}
                  <td style={{ padding: '4px', borderRight: '1px solid #cbd5e1' }}>
                    <input
                      type="number"
                      value={paketInput.biayadasar}
                      onChange={(e) => setPaketInput({ ...paketInput, biayadasar: parseFloat(e.target.value) || 0 })}
                      style={{
                        width: '100%',
                        border: '1px solid #94a3b8',
                        borderRadius: '2px',
                        padding: '2px 4px',
                        fontSize: '0.74rem',
                        fontWeight: '800',
                        textAlign: 'right',
                        color: '#1d4ed8'
                      }}
                    />
                  </td>

                  {/* SEWA ALAT */}
                  <td style={{ padding: '4px', borderRight: '1px solid #cbd5e1' }}>
                    <input
                      type="number"
                      value={paketInput.alat}
                      onChange={(e) => setPaketInput({ ...paketInput, alat: parseFloat(e.target.value) || 0 })}
                      style={{
                        width: '100%',
                        border: '1px solid #94a3b8',
                        borderRadius: '2px',
                        padding: '2px 4px',
                        fontSize: '0.74rem',
                        fontWeight: '800',
                        textAlign: 'right',
                        color: '#1d4ed8'
                      }}
                    />
                  </td>

                  {/* BENTUK */}
                  <td style={{ padding: '4px', borderRight: '1px solid #cbd5e1' }}>
                    <select
                      value={paketInput.bentuk}
                      onChange={(e) => setPaketInput({ ...paketInput, bentuk: e.target.value })}
                      style={{
                        width: '100%',
                        border: '1px solid #94a3b8',
                        borderRadius: '2px',
                        padding: '2px 2px',
                        fontSize: '0.72rem',
                        fontWeight: '600'
                      }}
                    >
                      <option value="-">-</option>
                      <option value="TEST">TEST</option>
                      <option value="VIAL">VIAL</option>
                      <option value="AMPULE">AMPULE</option>
                      <option value="STRIP">STRIP</option>
                      <option value="PCS">PCS</option>
                      <option value="BOTOL">BOTOL</option>
                      <option value="KIT">KIT</option>
                      <option value="TABUNG">TABUNG</option>
                      <option value="PACK">PACK</option>
                    </select>
                  </td>

                  {/* BAHAN RP */}
                  <td style={{ padding: '4px', borderRight: '1px solid #cbd5e1' }}>
                    <input
                      type="number"
                      value={paketInput.bahan}
                      onChange={(e) => setPaketInput({ ...paketInput, bahan: parseFloat(e.target.value) || 0 })}
                      style={{
                        width: '100%',
                        border: '1px solid #94a3b8',
                        borderRadius: '2px',
                        padding: '2px 4px',
                        fontSize: '0.74rem',
                        fontWeight: '800',
                        textAlign: 'right',
                        color: '#1d4ed8'
                      }}
                    />
                  </td>

                  {/* TOTAL TARIF */}
                  <td style={{ padding: '4px', borderRight: '1px solid #cbd5e1' }}>
                    <input
                      type="text"
                      readOnly
                      value={paketInput.biaya.toLocaleString('id-ID')}
                      style={{
                        width: '100%',
                        border: '1px solid #94a3b8',
                        borderRadius: '2px',
                        padding: '2px 4px',
                        fontSize: '0.74rem',
                        fontWeight: '800',
                        textAlign: 'right',
                        color: '#1d4ed8',
                        background: '#e2e8f0'
                      }}
                    />
                  </td>

                  {/* ACTION TAMBAH */}
                  <td style={{ padding: '4px', textAlign: 'center' }}>
                    <button
                      type="button"
                      style={{
                        background: '#047857',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '2px',
                        padding: '2px 8px',
                        fontSize: '0.72rem',
                        fontWeight: '700',
                        cursor: 'pointer'
                      }}
                      onClick={handleAddPaketItem}
                      title="Simpan Item ke Paket"
                    >
                      <i className="bi bi-plus-lg"></i>
                    </button>
                  </td>
                </tr>
              </thead>

              {/* TABLE BODY (TabelPeriksa) */}
              <tbody>
                {isPaketLoading ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: '30px', color: '#64748b' }}>
                      <Spinner size="sm" animation="border" className="me-2" />
                      Memuat data paket laboratorium...
                    </td>
                  </tr>
                ) : paketTarifList.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: '30px', color: '#94a3b8', fontStyle: 'italic' }}>
                      Belum ada data tindakan laboratorium untuk paket ini.
                    </td>
                  </tr>
                ) : (
                  paketTarifList.map((item, idx) => (
                    <tr
                      key={item.KD_TINDAKAN || idx}
                      style={{
                        background: idx % 2 === 0 ? '#ffffff' : '#f8fafc',
                        borderBottom: '1px solid #e2e8f0'
                      }}
                    >
                      <td style={{ padding: '5px 8px', borderRight: '1px solid #e2e8f0', fontWeight: '600' }}>
                        <span style={{ color: '#004b87', fontWeight: '700', marginRight: '6px' }}>
                          {item.KD_TINDAKAN}
                        </span>
                        {item.NM_TINDAKAN}
                      </td>
                      <td style={{ padding: '5px 8px', textAlign: 'right', borderRight: '1px solid #e2e8f0', fontWeight: '700' }}>
                        {(item.BIAYADASAR || 0).toLocaleString('id-ID')}
                      </td>
                      <td style={{ padding: '5px 8px', textAlign: 'right', borderRight: '1px solid #e2e8f0', fontWeight: '700' }}>
                        {(item.ALAT || 0).toLocaleString('id-ID')}
                      </td>
                      <td style={{ padding: '5px 8px', textAlign: 'center', borderRight: '1px solid #e2e8f0', fontWeight: '600' }}>
                        {item.BENTUK || '-'}
                      </td>
                      <td style={{ padding: '5px 8px', textAlign: 'right', borderRight: '1px solid #e2e8f0', fontWeight: '700' }}>
                        {(item.BAHAN || 0).toLocaleString('id-ID')}
                      </td>
                      <td style={{ padding: '5px 8px', textAlign: 'right', borderRight: '1px solid #e2e8f0', fontWeight: '800', color: '#047857' }}>
                        {(item.BIAYA || 0).toLocaleString('id-ID')}
                      </td>
                      <td style={{ padding: '5px 8px', textAlign: 'center' }}>
                        <button
                          type="button"
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: '#ef4444',
                            cursor: 'pointer'
                          }}
                          onClick={() => handleDeletePaketItem(item)}
                          title="Hapus item dari paket"
                        >
                          <i className="bi bi-trash3"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* DIGITAL GREEN TOTAL DISPLAY BOX (EXACT AS IMAGE) */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', paddingRight: '4px' }}>
          <div style={{
            background: '#111827',
            border: '1px solid #374151',
            borderRadius: '3px',
            padding: '4px 16px',
            minWidth: '150px',
            textAlign: 'right',
            boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.6)'
          }}>
            <span style={{
              fontFamily: "'Courier New', Courier, monospace",
              fontSize: '1.05rem',
              fontWeight: '800',
              color: '#22c55e',
              letterSpacing: '1px'
            }}>
              {paketTarifList.reduce((acc, curr) => acc + (parseFloat(curr.BIAYA) || 0), 0).toLocaleString('id-ID')}
            </span>
          </div>
        </div>

      </div>

      {/* BOTTOM ACTION BAR (EXACT AS IMAGE) */}
      <div style={{
        background: '#d1d5db',
        borderTop: '2px solid #9ca3af',
        padding: '6px 16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ color: '#991b1b', fontWeight: '800', fontSize: '0.78rem', letterSpacing: '0.5px' }}>
          Tombol [Esc] Untuk Keluar
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {/* CETAK */}
          <button
            type="button"
            style={{
              background: '#f8fafc',
              border: '1px solid #94a3b8',
              borderRadius: '4px',
              padding: '4px 14px',
              fontSize: '0.76rem',
              fontWeight: '700',
              color: '#1e293b',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              cursor: 'pointer',
              boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
            }}
            onClick={() => handlePrint(false)}
          >
            <i className="bi bi-printer text-dark"></i>
            CETAK
          </button>
          {/* SIMPAN */}
          <button
            type="button"
            style={{
              background: '#f8fafc',
              border: '1px solid #94a3b8',
              borderRadius: '4px',
              padding: '4px 14px',
              fontSize: '0.76rem',
              fontWeight: '700',
              color: '#1e293b',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              cursor: 'pointer',
              boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
            }}
            onClick={handleSaveAllPaket}
          >
            <i className="bi bi-floppy text-success"></i>
            SIMPAN
          </button>
        </div>
      </div>

    </div>
  )}

  {/* ═══════════════════════════════════════════════════════════ */}
  {/* ══ TAB 3: TARIF LABORATORIUM RUJUKAN (media_1788255665952) ══ */}
  {/* ═══════════════════════════════════════════════════════════ */}
  {activeTab === 'RUJUKAN' && (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      
      {/* TOP TITLE BANNER */}
      <div style={{
        background: '#002b49',
        color: '#ffffff',
        textAlign: 'center',
        padding: '6px 0',
        fontWeight: '800',
        fontSize: '0.86rem',
        letterSpacing: '1px',
        borderBottom: '2px solid #0284c7'
      }}>
        TARIF LABORATORIUM RUJUKAN
      </div>

      <div style={{ padding: '10px 14px', flex: 1, display: 'flex', flexDirection: 'column', gap: '10px' }}>
        
        {/* TOP SELECTION: KODE LABORATORIUM & JENIS PEMERIKSAAN */}
        <div style={{
          background: '#d1d5db',
          border: '1px solid #9ca3af',
          borderRadius: '4px',
          padding: '8px 14px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <div style={{ width: '100%', maxWidth: '620px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
            
            {/* ROW 1: KODE LABORATORIUM */}
            <div style={{ display: 'flex', alignItems: 'stretch', height: '28px' }}>
              <div style={{
                background: '#004b87',
                color: '#fff',
                fontWeight: '800',
                fontSize: '0.74rem',
                width: '160px',
                display: 'flex',
                alignItems: 'center',
                paddingLeft: '10px',
                borderRadius: '3px 0 0 3px',
                letterSpacing: '0.3px'
              }}>
                KODE LABORATORIUM
              </div>
              <input
                type="text"
                readOnly
                value={selectedLabRujukan.kd}
                style={{
                  width: '50px',
                  background: '#fff',
                  border: '1px solid #94a3b8',
                  borderLeft: 'none',
                  textAlign: 'center',
                  fontSize: '0.74rem',
                  fontWeight: '700',
                  outline: 'none'
                }}
              />
              <select
                style={{
                  flex: 1,
                  background: '#fff',
                  border: '1px solid #94a3b8',
                  borderLeft: 'none',
                  borderRadius: '0 3px 3px 0',
                  fontSize: '0.74rem',
                  fontWeight: '600',
                  padding: '0 6px',
                  outline: 'none'
                }}
                value={selectedLabRujukan.kd}
                onChange={(e) => {
                  const val = e.target.value;
                  const match = labRujukanList.find(l => (l.kode || l.KODE) === val);
                  setSelectedLabRujukan({ kd: val, nm: match ? (match.nama || match.NM_LAB) : '' });
                }}
              >
                <option value="">-- Pilih Laboratorium Rujukan --</option>
                {labRujukanList.map((l, i) => (
                  <option key={i} value={l.kode || l.KODE}>
                    {l.kode || l.KODE ? `${l.kode || l.KODE} - ` : ''}{l.nama || l.NM_LAB}
                  </option>
                ))}
              </select>
            </div>

            {/* ROW 2: JENIS PEMERIKSAAN */}
            <div style={{ display: 'flex', alignItems: 'stretch', height: '28px' }}>
              <div style={{
                background: '#004b87',
                color: '#fff',
                fontWeight: '800',
                fontSize: '0.74rem',
                width: '160px',
                display: 'flex',
                alignItems: 'center',
                paddingLeft: '10px',
                borderRadius: '3px 0 0 3px',
                letterSpacing: '0.3px'
              }}>
                JENIS PEMERIKSAAN
              </div>
              <input
                type="text"
                readOnly
                value={selectedRujukanJenis.kd}
                style={{
                  width: '50px',
                  background: '#fff',
                  border: '1px solid #94a3b8',
                  borderLeft: 'none',
                  textAlign: 'center',
                  fontSize: '0.74rem',
                  fontWeight: '700',
                  outline: 'none'
                }}
              />
              <select
                style={{
                  flex: 1,
                  background: '#fff',
                  border: '1px solid #94a3b8',
                  borderLeft: 'none',
                  borderRadius: '0 3px 3px 0',
                  fontSize: '0.74rem',
                  fontWeight: '600',
                  padding: '0 6px',
                  outline: 'none'
                }}
                value={selectedRujukanJenis.kd}
                onChange={(e) => {
                  const val = e.target.value;
                  const match = jenisLabOpts.find(j => (j.kode || j.kd_jenis || j.jenis) === val);
                  setSelectedRujukanJenis({ kd: val, nm: match ? (match.jenis || match.nm_jenis || val) : '' });
                }}
              >
                <option value="">-- Semua Jenis Pemeriksaan --</option>
                {jenisLabOpts.map((j, i) => (
                  <option key={i} value={j.kode || j.kd_jenis || j.jenis}>
                    {j.kode ? `${j.kode} - ` : ''}{j.jenis || j.nm_jenis}
                  </option>
                ))}
              </select>
            </div>

          </div>
        </div>

        {/* MAIN TABLE CONTAINER */}
        <div style={{
          background: '#ffffff',
          border: '1px solid #94a3b8',
          borderRadius: '4px',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 2px 4px rgba(0,0,0,0.06)',
          overflow: 'hidden',
          flex: 1
        }}>
          <div style={{ overflowX: 'auto', flex: 1, maxHeight: 'calc(100vh - 290px)', minHeight: '320px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.74rem' }}>
              {/* TEAL HEADER */}
              <thead>
                <tr style={{ background: '#007580', color: '#ffffff', textAlign: 'center', fontWeight: '800' }}>
                  <th rowSpan={2} style={{ padding: '6px 8px', borderRight: '1px solid #005f68', width: '48%' }}>
                    NAMA PELAYANAN DI BUKU TARIF RUMAH SAKIT YG BERLAKU
                  </th>
                  <th rowSpan={2} style={{ padding: '6px 8px', borderRight: '1px solid #005f68', width: '14%' }}>
                    BIAYA AWAL [Rp]
                  </th>
                  <th colSpan={2} style={{ padding: '4px 8px', borderRight: '1px solid #005f68', width: '22%', borderBottom: '1px solid #005f68' }}>
                    DISCOUNT
                  </th>
                  <th rowSpan={2} style={{ padding: '6px 8px', borderRight: '1px solid #005f68', width: '14%' }}>
                    BIAYA NETT [Rp]
                  </th>
                  <th rowSpan={2} style={{ padding: '6px 8px', width: '50px' }}>
                    AKSI
                  </th>
                </tr>
                <tr style={{ background: '#007580', color: '#ffffff', textAlign: 'center', fontWeight: '800' }}>
                  <th style={{ padding: '3px 6px', borderRight: '1px solid #005f68', width: '13%' }}>
                    NOMINAL [Rp]
                  </th>
                  <th style={{ padding: '3px 6px', borderRight: '1px solid #005f68', width: '9%' }}>
                    %
                  </th>
                </tr>

                {/* SUB-INPUT ROW (EXACT AS IMAGE) */}
                <tr style={{ background: '#f1f5f9', borderBottom: '2px solid #cbd5e1' }}>
                  {/* NAMA PELAYANAN INPUT */}
                  <td style={{ padding: '4px', borderRight: '1px solid #cbd5e1' }}>
                    <div style={{ display: 'flex', gap: '3px' }}>
                      <input
                        type="text"
                        placeholder="Kode"
                        value={rujukanInput.kd_tindakan}
                        onChange={(e) => setRujukanInput({ ...rujukanInput, kd_tindakan: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === 'F1' || e.key === 'Enter') {
                            setLookupTarget('RUJUKAN');
                            setLookupSearch('');
                            setShowLookupModal(true);
                          }
                        }}
                        style={{
                          width: '75px',
                          border: '1px solid #94a3b8',
                          borderRadius: '2px',
                          padding: '2px 4px',
                          fontSize: '0.72rem',
                          fontWeight: '700',
                          textAlign: 'center'
                        }}
                      />
                      <input
                        type="text"
                        placeholder="Nama Pelayanan (Klik / F1 untuk Lookup)"
                        value={rujukanInput.nm_tindakan}
                        readOnly
                        onClick={() => {
                          setLookupTarget('RUJUKAN');
                          setLookupSearch('');
                          setShowLookupModal(true);
                        }}
                        style={{
                          flex: 1,
                          border: '1px solid #94a3b8',
                          borderRadius: '2px',
                          padding: '2px 6px',
                          fontSize: '0.72rem',
                          fontWeight: '600',
                          background: '#fff',
                          cursor: 'pointer'
                        }}
                      />
                      <button
                        type="button"
                        style={{
                          border: '1px solid #94a3b8',
                          background: '#e2e8f0',
                          padding: '2px 6px',
                          borderRadius: '2px',
                          cursor: 'pointer'
                        }}
                        onClick={() => {
                          setLookupTarget('RUJUKAN');
                          setLookupSearch('');
                          setShowLookupModal(true);
                        }}
                        title="Buka Pencarian Tindakan (F1)"
                      >
                        <i className="bi bi-search"></i>
                      </button>
                    </div>
                  </td>

                  {/* BIAYA AWAL */}
                  <td style={{ padding: '4px', borderRight: '1px solid #cbd5e1' }}>
                    <input
                      type="number"
                      value={rujukanInput.biaya}
                      onChange={(e) => {
                        const b = parseFloat(e.target.value) || 0;
                        const discVal = parseFloat(rujukanInput.disc) || 0;
                        const discRp = (b * discVal) / 100.0;
                        const nett = Math.max(0, b - discRp);
                        setRujukanInput({ ...rujukanInput, biaya: b, discrp: discRp, harga: nett });
                      }}
                      style={{
                        width: '100%',
                        border: '1px solid #94a3b8',
                        borderRadius: '2px',
                        padding: '2px 4px',
                        fontSize: '0.74rem',
                        fontWeight: '800',
                        textAlign: 'right',
                        color: '#1d4ed8'
                      }}
                    />
                  </td>

                  {/* DISCOUNT NOMINAL [Rp] */}
                  <td style={{ padding: '4px', borderRight: '1px solid #cbd5e1' }}>
                    <input
                      type="number"
                      value={rujukanInput.discrp}
                      onChange={(e) => {
                        const drp = parseFloat(e.target.value) || 0;
                        const b = parseFloat(rujukanInput.biaya) || 0;
                        const discPct = b > 0 ? (drp / b) * 100.0 : 0;
                        const nett = Math.max(0, b - drp);
                        setRujukanInput({ ...rujukanInput, discrp: drp, disc: parseFloat(discPct.toFixed(2)), harga: nett });
                      }}
                      style={{
                        width: '100%',
                        border: '1px solid #94a3b8',
                        borderRadius: '2px',
                        padding: '2px 4px',
                        fontSize: '0.74rem',
                        fontWeight: '800',
                        textAlign: 'right',
                        color: '#1d4ed8'
                      }}
                    />
                  </td>

                  {/* DISCOUNT % */}
                  <td style={{ padding: '4px', borderRight: '1px solid #cbd5e1' }}>
                    <input
                      type="number"
                      value={rujukanInput.disc}
                      onChange={(e) => {
                        const pct = parseFloat(e.target.value) || 0;
                        const b = parseFloat(rujukanInput.biaya) || 0;
                        const discRp = (b * pct) / 100.0;
                        const nett = Math.max(0, b - discRp);
                        setRujukanInput({ ...rujukanInput, disc: pct, discrp: discRp, harga: nett });
                      }}
                      style={{
                        width: '100%',
                        border: '1px solid #94a3b8',
                        borderRadius: '2px',
                        padding: '2px 4px',
                        fontSize: '0.74rem',
                        fontWeight: '800',
                        textAlign: 'right',
                        color: '#1d4ed8'
                      }}
                    />
                  </td>

                  {/* BIAYA NETT */}
                  <td style={{ padding: '4px', borderRight: '1px solid #cbd5e1' }}>
                    <input
                      type="text"
                      readOnly
                      value={rujukanInput.harga.toLocaleString('id-ID')}
                      style={{
                        width: '100%',
                        border: '1px solid #94a3b8',
                        borderRadius: '2px',
                        padding: '2px 4px',
                        fontSize: '0.74rem',
                        fontWeight: '800',
                        textAlign: 'right',
                        color: '#1d4ed8',
                        background: '#e2e8f0'
                      }}
                    />
                  </td>

                  {/* ACTION TAMBAH */}
                  <td style={{ padding: '4px', textAlign: 'center' }}>
                    <button
                      type="button"
                      style={{
                        background: '#047857',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '2px',
                        padding: '2px 8px',
                        fontSize: '0.72rem',
                        fontWeight: '700',
                        cursor: 'pointer'
                      }}
                      onClick={handleAddRujukanItem}
                      title="Simpan Item ke Tarif Rujukan"
                    >
                      <i className="bi bi-plus-lg"></i>
                    </button>
                  </td>
                </tr>
              </thead>

              {/* TABLE BODY (TabelPeriksa) */}
              <tbody>
                {isRujukanLoading ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: '30px', color: '#64748b' }}>
                      <Spinner size="sm" animation="border" className="me-2" />
                      Memuat data tarif rujukan laboratorium...
                    </td>
                  </tr>
                ) : rujukanTarifList.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: '30px', color: '#94a3b8', fontStyle: 'italic' }}>
                      Belum ada data tarif laboratorium rujukan untuk pilihan di atas.
                    </td>
                  </tr>
                ) : (
                  rujukanTarifList.map((item, idx) => (
                    <tr
                      key={item.KD_TINDAKAN || idx}
                      style={{
                        background: idx % 2 === 0 ? '#ffffff' : '#f8fafc',
                        borderBottom: '1px solid #e2e8f0'
                      }}
                    >
                      <td style={{ padding: '5px 8px', borderRight: '1px solid #e2e8f0', fontWeight: '600' }}>
                        <span style={{ color: '#004b87', fontWeight: '700', marginRight: '6px' }}>
                          {item.KD_TINDAKAN}
                        </span>
                        {item.NM_TINDAKAN}
                      </td>
                      <td style={{ padding: '5px 8px', textAlign: 'right', borderRight: '1px solid #e2e8f0', fontWeight: '700' }}>
                        {(item.BIAYA || 0).toLocaleString('id-ID')}
                      </td>
                      <td style={{ padding: '5px 8px', textAlign: 'right', borderRight: '1px solid #e2e8f0', fontWeight: '700' }}>
                        {(item.DISCRP || 0).toLocaleString('id-ID')}
                      </td>
                      <td style={{ padding: '5px 8px', textAlign: 'center', borderRight: '1px solid #e2e8f0', fontWeight: '600' }}>
                        {item.DISC ? `${item.DISC}%` : '0%'}
                      </td>
                      <td style={{ padding: '5px 8px', textAlign: 'right', borderRight: '1px solid #e2e8f0', fontWeight: '800', color: '#047857' }}>
                        {(item.HARGA || 0).toLocaleString('id-ID')}
                      </td>
                      <td style={{ padding: '5px 8px', textAlign: 'center' }}>
                        <button
                          type="button"
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: '#ef4444',
                            cursor: 'pointer'
                          }}
                          onClick={() => handleDeleteRujukanItem(item)}
                          title="Hapus tarif rujukan"
                        >
                          <i className="bi bi-trash3"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>

      {/* BOTTOM ACTION BAR (EXACT AS media_1788255665952.png: SIMPAN, HAPUS, CETAK, KELUAR) */}
      <div style={{
        background: '#4b5563',
        borderTop: '2px solid #374151',
        padding: '6px 16px',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: '8px'
      }}>
        {/* SIMPAN */}
        <button
          type="button"
          style={{
            background: '#e2e8f0',
            border: '1px solid #94a3b8',
            borderRadius: '4px',
            padding: '4px 14px',
            fontSize: '0.76rem',
            fontWeight: '700',
            color: '#0f172a',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            cursor: 'pointer',
            boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
          }}
          onClick={handleAddRujukanItem}
        >
          <i className="bi bi-floppy text-dark"></i>
          SIMPAN
        </button>

        {/* HAPUS */}
        <button
          type="button"
          style={{
            background: '#e2e8f0',
            border: '1px solid #94a3b8',
            borderRadius: '4px',
            padding: '4px 14px',
            fontSize: '0.76rem',
            fontWeight: '700',
            color: '#0f172a',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            cursor: 'pointer',
            boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
          }}
          onClick={handleClearRujukan}
        >
          <i className="bi bi-x-square-fill text-danger"></i>
          HAPUS
        </button>

        {/* CETAK */}
        <button
          type="button"
          style={{
            background: '#e2e8f0',
            border: '1px solid #94a3b8',
            borderRadius: '4px',
            padding: '4px 14px',
            fontSize: '0.76rem',
            fontWeight: '700',
            color: '#0f172a',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            cursor: 'pointer',
            boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
          }}
          onClick={() => handlePrint(false)}
        >
          <i className="bi bi-printer text-dark"></i>
          CETAK
        </button>

        {/* KELUAR */}
        <button
          type="button"
          style={{
            background: '#e2e8f0',
            border: '1px solid #94a3b8',
            borderRadius: '4px',
            padding: '4px 14px',
            fontSize: '0.76rem',
            fontWeight: '700',
            color: '#0f172a',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            cursor: 'pointer',
            boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
          }}
          onClick={() => navigate('/home')}
        >
          <i className="bi bi-x-circle-fill text-danger"></i>
          KELUAR
        </button>
      </div>

    </div>
  )}

      {/* ══ MODAL SALIN TARIF (EXACT AS USER IMAGE) ══ */}
      <Modal show={showSalinModal} onHide={() => setShowSalinModal(false)} centered size="lg" backdrop="static" keyboard={false}>
        <Modal.Body className="p-0" style={{ background: '#cbd5e1', borderRadius: '4px', overflow: 'hidden', border: '1px solid #64748b' }}>
          
          {/* TITLE BAR */}
          <div style={{
            background: 'linear-gradient(180deg, #003652 0%, #002235 100%)',
            color: '#fff',
            textAlign: 'center',
            padding: '6px 0',
            fontWeight: '800',
            fontSize: '0.86rem',
            letterSpacing: '1px',
            borderBottom: '2px solid #0284c7'
          }}>
            SALIN TARIF LABORATORIUM
          </div>

          <div style={{ padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            
            {/* SECTION 1: SALIN TARIF DARI */}
            <div style={{ border: '1px solid #94a3b8', background: '#e2e8f0' }}>
              <div style={{
                background: '#005b7f',
                color: '#fff',
                fontWeight: '800',
                fontSize: '0.76rem',
                textAlign: 'center',
                padding: '3px 0',
                letterSpacing: '0.5px'
              }}>
                SALIN TARIF DARI :
              </div>
              <div style={{ padding: '4px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                
                {/* KLASIFIKASI TARIF */}
                <div style={{ display: 'flex', alignItems: 'stretch', height: '26px' }}>
                  <div style={{ width: '170px', background: '#004b87', color: '#fff', fontSize: '0.72rem', fontWeight: '700', padding: '0 8px', display: 'flex', alignItems: 'center' }}>
                    KLASIFIKASI TARIF
                  </div>
                  <input
                    type="text"
                    style={{ width: '50px', background: '#fff', border: '1px solid #94a3b8', textAlign: 'center', fontSize: '0.74rem', fontWeight: '700' }}
                    value={salinPayload.src_klasifikasi}
                    onChange={e => {
                      const val = e.target.value;
                      setSalinPayload({ ...salinPayload, src_klasifikasi: val });
                    }}
                  />
                  <select
                    style={{ flex: 1, background: '#fff', border: '1px solid #94a3b8', borderLeft: 'none', fontSize: '0.74rem', fontWeight: '600', padding: '0 6px' }}
                    value={salinPayload.src_klasifikasi}
                    onChange={e => setSalinPayload({ ...salinPayload, src_klasifikasi: e.target.value })}
                  >
                    <option value="">-- Pilih Klasifikasi Sumber --</option>
                    {klasifikasiOpts.map((k, i) => {
                      const kd = k.kd_klas || k.kd_klasifikasi || k.kode;
                      const nm = k.nm_klas || k.nm_klasifikasi || k.nama;
                      return <option key={i} value={kd}>{kd} - {nm}</option>;
                    })}
                  </select>
                </div>

                {/* KELAS TARIF */}
                <div style={{ display: 'flex', alignItems: 'stretch', height: '26px' }}>
                  <div style={{ width: '170px', background: '#004b87', color: '#fff', fontSize: '0.72rem', fontWeight: '700', padding: '0 8px', display: 'flex', alignItems: 'center' }}>
                    KELAS TARIF
                  </div>
                  <input
                    type="text"
                    style={{ width: '50px', background: '#fff', border: '1px solid #94a3b8', textAlign: 'center', fontSize: '0.74rem', fontWeight: '700' }}
                    value={salinPayload.src_kelas}
                    onChange={e => {
                      const val = e.target.value;
                      setSalinPayload({ ...salinPayload, src_kelas: val });
                    }}
                  />
                  <select
                    style={{ flex: 1, background: '#fff', border: '1px solid #94a3b8', borderLeft: 'none', fontSize: '0.74rem', fontWeight: '600', padding: '0 6px' }}
                    value={salinPayload.src_kelas}
                    onChange={e => setSalinPayload({ ...salinPayload, src_kelas: e.target.value })}
                  >
                    <option value="">-- Pilih Kelas Sumber --</option>
                    {kelasOpts.map((k, i) => {
                      const kd = k.kd_klas || k.kd_kelas || k.kode;
                      const nm = k.nm_klas || k.nm_kelas || k.nama;
                      return <option key={i} value={kd}>{kd} - {nm}</option>;
                    })}
                  </select>
                </div>

              </div>
            </div>

            {/* SECTION 2: UNTUK : */}
            <div style={{ border: '1px solid #94a3b8', background: '#e2e8f0' }}>
              <div style={{
                background: '#005b7f',
                color: '#fff',
                fontWeight: '800',
                fontSize: '0.76rem',
                textAlign: 'center',
                padding: '3px 0',
                letterSpacing: '0.5px'
              }}>
                UNTUK :
              </div>
              <div style={{ padding: '4px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                
                {/* KLASIFIKASI TARIF */}
                <div style={{ display: 'flex', alignItems: 'stretch', height: '26px' }}>
                  <div style={{ width: '170px', background: '#004b87', color: '#fff', fontSize: '0.72rem', fontWeight: '700', padding: '0 8px', display: 'flex', alignItems: 'center' }}>
                    KLASIFIKASI TARIF
                  </div>
                  <input
                    type="text"
                    style={{ width: '50px', background: '#fff', border: '1px solid #94a3b8', textAlign: 'center', fontSize: '0.74rem', fontWeight: '700' }}
                    value={salinPayload.dst_klasifikasi}
                    onChange={e => {
                      const val = e.target.value;
                      setSalinPayload({ ...salinPayload, dst_klasifikasi: val });
                    }}
                  />
                  <select
                    style={{ flex: 1, background: '#fff', border: '1px solid #94a3b8', borderLeft: 'none', fontSize: '0.74rem', fontWeight: '600', padding: '0 6px' }}
                    value={salinPayload.dst_klasifikasi}
                    onChange={e => setSalinPayload({ ...salinPayload, dst_klasifikasi: e.target.value })}
                  >
                    <option value="">-- Pilih Klasifikasi Tujuan --</option>
                    {klasifikasiOpts.map((k, i) => {
                      const kd = k.kd_klas || k.kd_klasifikasi || k.kode;
                      const nm = k.nm_klas || k.nm_klasifikasi || k.nama;
                      return <option key={i} value={kd}>{kd} - {nm}</option>;
                    })}
                  </select>
                </div>

                {/* KELAS TARIF */}
                <div style={{ display: 'flex', alignItems: 'stretch', height: '26px' }}>
                  <div style={{ width: '170px', background: '#004b87', color: '#fff', fontSize: '0.72rem', fontWeight: '700', padding: '0 8px', display: 'flex', alignItems: 'center' }}>
                    KELAS TARIF
                  </div>
                  <input
                    type="text"
                    style={{ width: '50px', background: '#fff', border: '1px solid #94a3b8', textAlign: 'center', fontSize: '0.74rem', fontWeight: '700' }}
                    value={salinPayload.dst_kelas}
                    onChange={e => {
                      const val = e.target.value;
                      setSalinPayload({ ...salinPayload, dst_kelas: val });
                    }}
                  />
                  <select
                    style={{ flex: 1, background: '#fff', border: '1px solid #94a3b8', borderLeft: 'none', fontSize: '0.74rem', fontWeight: '600', padding: '0 6px' }}
                    value={salinPayload.dst_kelas}
                    onChange={e => setSalinPayload({ ...salinPayload, dst_kelas: e.target.value })}
                  >
                    <option value="">-- Pilih Kelas Tujuan --</option>
                    {kelasOpts.map((k, i) => {
                      const kd = k.kd_klas || k.kd_kelas || k.kode;
                      const nm = k.nm_klas || k.nm_kelas || k.nama;
                      return <option key={i} value={kd}>{kd} - {nm}</option>;
                    })}
                  </select>
                </div>

              </div>
            </div>

            {/* SECTION 3: OPTIONS BAR (MAROON / RED BACKGROUND AS IMAGE) */}
            <div style={{
              background: '#700000',
              color: '#fff',
              padding: '6px 12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontWeight: '700',
              fontSize: '0.74rem'
            }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', margin: 0 }}>
                <input
                  type="radio"
                  name="opt_salin"
                  checked={!salinPayload.mode_kenaikan}
                  onChange={() => setSalinPayload({ ...salinPayload, mode_kenaikan: false, kenaikan_persen: 0 })}
                />
                <span>SALIN DENGAN TARIF AWAL</span>
              </label>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', margin: 0 }}>
                  <input
                    type="radio"
                    name="opt_salin"
                    checked={salinPayload.mode_kenaikan}
                    onChange={() => setSalinPayload({ ...salinPayload, mode_kenaikan: true })}
                  />
                  <span>SALIN DENGAN KENAIKAN</span>
                </label>
                <input
                  type="number"
                  style={{
                    width: '60px',
                    height: '24px',
                    textAlign: 'center',
                    fontWeight: '800',
                    background: '#fff',
                    color: '#000',
                    border: '1px solid #cbd5e1',
                    borderRadius: '2px'
                  }}
                  disabled={!salinPayload.mode_kenaikan}
                  value={salinPayload.kenaikan_persen}
                  onChange={e => setSalinPayload({ ...salinPayload, kenaikan_persen: e.target.value })}
                />
                <span>% DARI TARIF</span>
              </div>
            </div>

            {/* SECTION 4: BOTTOM BAR (EXIT, PROGRESS BAR, PROSES) */}
            <div style={{
              background: '#cbd5e1',
              padding: '6px 4px',
              display: 'grid',
              gridTemplateColumns: '170px 1fr 120px',
              alignItems: 'center',
              gap: '10px'
            }}>
              {/* EXIT HINT / BUTTON */}
              <button
                type="button"
                style={{
                  background: '#e2e8f0',
                  border: '1px solid #94a3b8',
                  padding: '4px 8px',
                  color: '#991b1b',
                  fontWeight: '800',
                  fontSize: '0.74rem',
                  cursor: 'pointer',
                  borderRadius: '3px'
                }}
                onClick={() => setShowSalinModal(false)}
              >
                Tombol [Esc] Untuk Keluar
              </button>

              {/* PROGRESS BAR */}
              <div style={{ background: '#fff', border: '1px solid #94a3b8', borderRadius: '3px', padding: '2px', height: '28px', display: 'flex', alignItems: 'center' }}>
                <ProgressBar
                  animated={isSalinLoading}
                  now={salinProgress}
                  label={`${salinProgress}%`}
                  style={{ width: '100%', height: '22px', fontSize: '0.72rem', fontWeight: '800' }}
                  variant="info"
                />
              </div>

              {/* PROSES BUTTON */}
              <button
                type="button"
                disabled={isSalinLoading}
                style={{
                  background: '#f8fafc',
                  border: '1px solid #64748b',
                  borderRadius: '3px',
                  height: '28px',
                  fontSize: '0.76rem',
                  fontWeight: '800',
                  color: '#0f172a',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  cursor: 'pointer',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.15)'
                }}
                onClick={handleProcessSalin}
              >
                {isSalinLoading ? (
                  <Spinner size="sm" animation="border" />
                ) : (
                  <i className="bi bi-gear-wide-connected text-primary"></i>
                )}
                PROSES
              </button>
            </div>

          </div>

        </Modal.Body>
      </Modal>

      {/* ══ MODAL RINCIAN BAHAN LABORATORIUM (EXACT AS USER IMAGE) ══ */}
      <Modal show={showRincianBahanModal} onHide={() => setShowRincianBahanModal(false)} centered size="xl" backdrop="static" keyboard={false}>
        <Modal.Body className="p-0" style={{ background: '#cbd5e1', borderRadius: '4px', overflow: 'hidden', border: '1px solid #64748b' }}>
          
          {/* TITLE BAR */}
          <div style={{
            background: 'linear-gradient(180deg, #003652 0%, #002235 100%)',
            color: '#fff',
            textAlign: 'center',
            padding: '6px 0',
            fontWeight: '800',
            fontSize: '0.86rem',
            letterSpacing: '1px',
            borderBottom: '2px solid #0284c7'
          }}>
            RINCIAN BAHAN PEMERIKSAAN
          </div>

          <div style={{ padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            
            {/* TEAL TABLE CONTAINER */}
            <div style={{ background: '#fff', border: '1px solid #94a3b8', borderRadius: '3px', overflow: 'hidden' }}>
              
              {/* TEAL TABLE HEADER */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(280px, 3fr) 100px 110px 90px 90px 120px 60px',
                background: T.tealHdr,
                color: '#fff',
                fontWeight: '700',
                fontSize: '0.74rem',
                textAlign: 'center',
                borderBottom: '1px solid rgba(0,0,0,0.2)'
              }}>
                <div style={{ padding: '6px', borderRight: '1px solid rgba(255,255,255,0.2)' }}>NAMA BARANG</div>
                <div style={{ padding: '6px', borderRight: '1px solid rgba(255,255,255,0.2)' }}>SATUAN</div>
                <div style={{ padding: '6px', borderRight: '1px solid rgba(255,255,255,0.2)' }}>H.D. [Rp]</div>
                <div style={{ padding: '6px', borderRight: '1px solid rgba(255,255,255,0.2)' }}>MARGIN</div>
                <div style={{ padding: '6px', borderRight: '1px solid rgba(255,255,255,0.2)' }}>QTY</div>
                <div style={{ padding: '6px', borderRight: '1px solid rgba(255,255,255,0.2)' }}>NILAI [Rp]</div>
                <div style={{ padding: '6px' }}>AKSI</div>
              </div>

              {/* INPUT ROW UNDER HEADER (WHITE/GREY AS IMAGE) */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(280px, 3fr) 100px 110px 90px 90px 120px 60px',
                background: '#fff',
                padding: '3px 2px',
                gap: '2px',
                borderBottom: '2px solid #94a3b8',
                alignItems: 'center'
              }}>
                {/* NAMA BARANG: KODE & NAMA */}
                <div style={{ display: 'flex', gap: '2px' }}>
                  <input
                    type="text"
                    placeholder="Kode"
                    style={{
                      width: '65px',
                      height: '26px',
                      background: '#fff',
                      border: '1px solid #94a3b8',
                      fontSize: '0.74rem',
                      fontWeight: '700',
                      textAlign: 'center',
                      borderRadius: '2px'
                    }}
                    value={bahanInput.kd_obat}
                    onChange={e => setBahanInput({ ...bahanInput, kd_obat: e.target.value })}
                  />
                  <input
                    type="text"
                    placeholder="Nama Barang / Obat..."
                    style={{
                      flex: 1,
                      height: '26px',
                      background: '#fff',
                      border: '1px solid #94a3b8',
                      fontSize: '0.74rem',
                      fontWeight: '600',
                      padding: '0 6px',
                      borderRadius: '2px'
                    }}
                    value={bahanInput.nm_obat}
                    onChange={e => setBahanInput({ ...bahanInput, nm_obat: e.target.value })}
                    onKeyDown={e => e.key === 'Enter' && handleAddBahanItem()}
                  />
                </div>

                {/* SATUAN */}
                <div>
                  <input
                    type="text"
                    placeholder="Satuan"
                    style={{
                      width: '100%',
                      height: '26px',
                      background: '#fff',
                      border: '1px solid #94a3b8',
                      fontSize: '0.74rem',
                      textAlign: 'center',
                      borderRadius: '2px'
                    }}
                    value={bahanInput.satuan}
                    onChange={e => setBahanInput({ ...bahanInput, satuan: e.target.value })}
                  />
                </div>

                {/* H.D. [Rp] */}
                <div>
                  <input
                    type="number"
                    style={{
                      width: '100%',
                      height: '26px',
                      background: '#fff',
                      border: '1px solid #94a3b8',
                      fontSize: '0.76rem',
                      fontWeight: '700',
                      textAlign: 'right',
                      padding: '0 6px',
                      color: '#1d4ed8',
                      borderRadius: '2px'
                    }}
                    value={bahanInput.hargadasar}
                    onChange={e => setBahanInput({ ...bahanInput, hargadasar: e.target.value })}
                    onKeyDown={e => e.key === 'Enter' && handleAddBahanItem()}
                  />
                </div>

                {/* MARGIN */}
                <div>
                  <input
                    type="number"
                    style={{
                      width: '100%',
                      height: '26px',
                      background: '#fff',
                      border: '1px solid #94a3b8',
                      fontSize: '0.76rem',
                      fontWeight: '700',
                      textAlign: 'right',
                      padding: '0 6px',
                      color: '#1d4ed8',
                      borderRadius: '2px'
                    }}
                    value={bahanInput.margin}
                    onChange={e => setBahanInput({ ...bahanInput, margin: e.target.value })}
                    onKeyDown={e => e.key === 'Enter' && handleAddBahanItem()}
                  />
                </div>

                {/* QTY */}
                <div>
                  <input
                    type="number"
                    style={{
                      width: '100%',
                      height: '26px',
                      background: '#fff',
                      border: '1px solid #94a3b8',
                      fontSize: '0.76rem',
                      fontWeight: '700',
                      textAlign: 'right',
                      padding: '0 6px',
                      color: '#1d4ed8',
                      borderRadius: '2px'
                    }}
                    value={bahanInput.qty}
                    onChange={e => setBahanInput({ ...bahanInput, qty: e.target.value })}
                    onKeyDown={e => e.key === 'Enter' && handleAddBahanItem()}
                  />
                </div>

                {/* NILAI [Rp] */}
                <div>
                  <input
                    type="text"
                    readOnly
                    style={{
                      width: '100%',
                      height: '26px',
                      background: '#f1f5f9',
                      border: '1px solid #94a3b8',
                      fontSize: '0.76rem',
                      fontWeight: '800',
                      textAlign: 'right',
                      padding: '0 6px',
                      color: '#1d4ed8',
                      borderRadius: '2px'
                    }}
                    value={fmt(bahanInput.harga)}
                  />
                </div>

                {/* BUTTON TAMBAH */}
                <div>
                  <Button
                    variant="success"
                    size="sm"
                    style={{ width: '100%', height: '26px', padding: 0, fontSize: '0.7rem', fontWeight: 700 }}
                    onClick={handleAddBahanItem}
                    title="Tambah Item Bahan"
                  >
                    <i className="bi bi-plus"></i>
                  </Button>
                </div>

              </div>

              {/* TABLE GRID AREA (Tabelperiksa2) */}
              <div style={{ minHeight: '260px', maxHeight: '340px', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                  <tbody>
                    {isRincianLoading ? (
                      <tr>
                        <td colSpan="8" className="text-center py-5 text-muted">
                          <Spinner animation="border" size="sm" className="me-2" variant="teal" />
                          Memuat rincian bahan...
                        </td>
                      </tr>
                    ) : rincianBahanList.length > 0 ? (
                      rincianBahanList.map((b, idx) => (
                        <tr
                          key={idx}
                          style={{
                            borderBottom: '1px solid #e2e8f0',
                            background: idx % 2 === 0 ? '#fff' : '#f8fafc'
                          }}
                        >
                          <td style={{ padding: '5px 8px', width: '35px', color: '#64748b', textAlign: 'center', borderRight: '1px solid #e2e8f0' }}>
                            {idx + 1}
                          </td>
                          <td style={{ padding: '5px 8px', borderRight: '1px solid #e2e8f0' }}>
                            <span className="font-monospace text-primary fw-bold me-2">[{b.kd_obat}]</span>
                            <span className="fw-semibold text-dark">{b.nm_obat}</span>
                          </td>
                          <td style={{ padding: '5px 8px', width: '100px', textAlign: 'center', borderRight: '1px solid #e2e8f0' }}>
                            {b.satuan || '-'}
                          </td>
                          <td style={{ padding: '5px 8px', width: '110px', textAlign: 'right', fontFamily: 'monospace', borderRight: '1px solid #e2e8f0' }}>
                            {fmt(b.hargadasar)}
                          </td>
                          <td style={{ padding: '5px 8px', width: '90px', textAlign: 'right', fontFamily: 'monospace', borderRight: '1px solid #e2e8f0' }}>
                            {b.margin}
                          </td>
                          <td style={{ padding: '5px 8px', width: '90px', textAlign: 'right', fontWeight: 700, borderRight: '1px solid #e2e8f0' }}>
                            {b.qty}
                          </td>
                          <td style={{ padding: '5px 8px', width: '120px', textAlign: 'right', fontWeight: 700, color: '#047857', borderRight: '1px solid #e2e8f0' }}>
                            {fmt(b.harga)}
                          </td>
                          <td style={{ padding: '4px', width: '60px', textAlign: 'center' }}>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              style={{ padding: '1px 6px', fontSize: '0.66rem' }}
                              onClick={() => handleDeleteBahanItem(b)}
                              title="Hapus Item Bahan"
                            >
                              <i className="bi bi-trash"></i>
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className="text-center py-5 text-muted">
                          <i className="bi bi-box-seam d-block fs-3 opacity-25 mb-1"></i>
                          Belum ada komponen rincian bahan terdaftar untuk tindakan ini.
                          <div className="text-muted mt-1" style={{ fontSize: '0.7rem' }}>
                            Gunakan baris input di atas untuk menambahkan bahan.
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

            </div>

            {/* TOTAL NILAI DISPLAY BOX (DARK RECTANGLE WITH GREEN TEXT AS IMAGE) */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', paddingRight: '2px' }}>
              <div style={{
                background: '#18181b',
                color: '#22c55e',
                fontFamily: "'Courier New', monospace",
                fontWeight: '900',
                fontSize: '1.05rem',
                padding: '4px 18px',
                borderRadius: '3px',
                border: '1px solid #3f3f46',
                minWidth: '150px',
                textAlign: 'right',
                letterSpacing: '1px',
                boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.6)'
              }}>
                {fmt(rincianBahanList.reduce((acc, curr) => acc + (parseFloat(curr.harga) || 0), 0))}
              </div>
            </div>

            {/* BOTTOM ACTION BAR (EXIT & PROSES BUTTON AS IMAGE) */}
            <div style={{
              background: '#cbd5e1',
              borderTop: '1px solid #94a3b8',
              padding: '6px 4px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              {/* EXIT HINT */}
              <button
                type="button"
                style={{
                  background: '#e2e8f0',
                  border: '1px solid #94a3b8',
                  padding: '4px 10px',
                  color: '#991b1b',
                  fontWeight: '800',
                  fontSize: '0.74rem',
                  cursor: 'pointer',
                  borderRadius: '3px'
                }}
                onClick={() => setShowRincianBahanModal(false)}
              >
                Tombol [Esc] Untuk Keluar
              </button>

              {/* PROSES BUTTON */}
              <button
                type="button"
                style={{
                  background: '#f8fafc',
                  border: '1px solid #64748b',
                  borderRadius: '3px',
                  height: '28px',
                  padding: '0 16px',
                  fontSize: '0.76rem',
                  fontWeight: '800',
                  color: '#0f172a',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  cursor: 'pointer',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.15)'
                }}
                onClick={handleProcessBahan}
              >
                <i className="bi bi-gear-wide-connected text-primary"></i>
                PROSES
              </button>
            </div>

          </div>

        </Modal.Body>
      </Modal>

      {/* ══ MODAL LOOKUP MASTER TINDAKAN LAB ══ */}
      <Modal show={showLookupModal} onHide={() => setShowLookupModal(false)} centered size="lg" keyboard={false}>
        <Modal.Header closeButton style={{ background: T.tealHdr, color: '#fff' }}>
          <Modal.Title style={{ fontSize: '0.9rem', fontWeight: '700' }} className="d-flex align-items-center gap-2">
            <i className="bi bi-search"></i>
            <span>Pilih Tindakan / Pemeriksaan Laboratorium</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3 bg-light">
          <InputGroup size="sm" className="mb-3">
            <InputGroup.Text><i className="bi bi-search"></i></InputGroup.Text>
            <FormControl
              placeholder="Cari kode atau nama pemeriksaan lab..."
              value={lookupSearch}
              onChange={e => setLookupSearch(e.target.value)}
              autoFocus
            />
          </InputGroup>

          <div style={{ maxHeight: '380px', overflowY: 'auto' }}>
            <Table hover striped bordered size="sm" className="align-middle mb-0" style={{ fontSize: '0.75rem' }}>
              <thead style={{ background: '#0f172a', color: '#fff', position: 'sticky', top: 0, zIndex: 1 }}>
                <tr>
                  <th className="text-center" style={{ width: '40px' }}>No</th>
                  <th className="text-center" style={{ width: '90px' }}>Kode</th>
                  <th>Nama Pemeriksaan</th>
                  <th>Jenis</th>
                  <th className="text-center" style={{ width: '70px' }}>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {tindakanLabList
                  .filter(t => {
                    if (!lookupSearch) return true;
                    const q = lookupSearch.toLowerCase();
                    const kd = (t.kode || t.kd_tindakan || '').toLowerCase();
                    const nm = (t.nm_tindakan || t.nmtindakan || '').toLowerCase();
                    const jn = (t.jenis || t.nmjenis || '').toLowerCase();
                    return kd.includes(q) || nm.includes(q) || jn.includes(q);
                  })
                  .slice(0, 100)
                  .map((item, idx) => (
                    <tr key={idx} style={{ cursor: 'pointer' }} onDoubleClick={() => handleSelectTindakan(item)}>
                      <td className="text-center text-muted">{idx + 1}</td>
                      <td className="text-center font-monospace fw-bold text-primary">{item.kode || item.kd_tindakan}</td>
                      <td className="fw-semibold text-dark">{item.nm_tindakan || item.nmtindakan}</td>
                      <td className="text-muted">{item.jenis || item.nmjenis || '-'}</td>
                      <td className="text-center">
                        <Button
                          variant="teal"
                          size="sm"
                          style={{ background: '#0d9488', color: '#fff', padding: '1px 8px', fontSize: '0.7rem', fontWeight: 700 }}
                          onClick={() => handleSelectTindakan(item)}
                        >
                          Pilih
                        </Button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
        <Modal.Footer className="p-2 bg-light">
          <Button variant="secondary" size="sm" onClick={() => setShowLookupModal(false)}>
            Tutup
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ══ MODAL CETAK / PREVIEW PDF TARIF LAB (IN-APP VIEWER) ══ */}
      <Modal show={showPrintModal} onHide={() => setShowPrintModal(false)} centered size="xl" backdrop="static" keyboard={false}>
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #003652 0%, #002235 100%)', color: '#fff', padding: '10px 16px' }}>
          <Modal.Title style={{ fontSize: '0.9rem', fontWeight: '800' }} className="d-flex align-items-center gap-2">
            <i className="bi bi-file-earmark-pdf-fill text-danger"></i>
            <span>Daftar Tarif Laboratorium (Preview Dokumen)</span>
          </Modal.Title>
        </Modal.Header>

        {/* TOP PROGRESS BAR IF LOADING */}
        {isPdfLoading && (
          <div style={{ background: '#e2e8f0', padding: '0' }}>
            <ProgressBar
              animated
              now={pdfProgress}
              variant="info"
              style={{ height: '4px', borderRadius: 0 }}
            />
          </div>
        )}

        <Modal.Body className="p-0 bg-secondary position-relative" style={{ height: '78vh', minHeight: '480px' }}>
          {isPdfLoading ? (
            <div className="d-flex flex-column align-items-center justify-content-center h-100 bg-light p-4 text-center">
              <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} className="mb-3" />
              <h6 className="fw-bold text-dark mb-2">
                {activeTab === 'RUJUKAN'
                  ? 'Menyiapkan Dokumen PDF Tarif Laboratorium Rujukan...'
                  : 'Menyiapkan Dokumen PDF Tarif Laboratorium...'}
              </h6>
              <div className="text-muted small mb-3" style={{ maxWidth: '450px' }}>
                {activeTab === 'RUJUKAN' ? (
                  <>Laboratorium: <strong>{selectedLabRujukan.nm || selectedLabRujukan.kd || '-'}</strong> | Jenis: <strong>{selectedRujukanJenis.nm || selectedRujukanJenis.kd || 'Semua'}</strong></>
                ) : (
                  <>Klasifikasi: <strong>{selectedKlasifikasi.nm}</strong> | Kelas: <strong>{selectedKelas.nm}</strong></>
                )}
              </div>
              <div style={{ width: '320px' }}>
                <ProgressBar
                  animated
                  now={pdfProgress}
                  label={`${pdfProgress}%`}
                  variant="primary"
                  style={{ height: '22px', fontWeight: '700', fontSize: '0.75rem' }}
                />
              </div>
            </div>
          ) : printPdfUrl ? (
            <iframe
              src={printPdfUrl}
              title="Cetak Tarif Lab"
              style={{ width: '100%', height: '100%', border: 'none', background: '#525659' }}
            />
          ) : (
            <div className="d-flex flex-column align-items-center justify-content-center h-100 bg-light text-muted">
              <i className="bi bi-file-earmark-x fs-1 text-danger mb-2"></i>
              <span>Gagal memuat preview dokumen PDF.</span>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="p-2 bg-light d-flex justify-content-between align-items-center">
          <span style={{ color: '#991b1b', fontWeight: '700', fontSize: '0.74rem' }}>
            Tombol [Esc] Untuk Keluar
          </span>
          <div className="d-flex gap-2 align-items-center">
            <Button
              variant="success"
              size="sm"
              className="fw-bold"
              onClick={() => handlePrint(true)}
            >
              <i className="bi bi-download me-1"></i> Unduh PDF
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowPrintModal(false)}
            >
              Tutup
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* ══ GLOBAL FLOATING TOAST NOTIFICATION ══ */}
      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 99999, position: 'fixed' }}>
        <Toast
          show={toastNotif.show}
          onClose={() => setToastNotif(prev => ({ ...prev, show: false }))}
          delay={4000}
          autohide
          style={{
            minWidth: '320px',
            maxWidth: '400px',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.2), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
            borderRadius: '8px',
            border: 'none',
            overflow: 'hidden'
          }}
        >
          <Toast.Header
            style={{
              background: toastNotif.type === 'success' ? '#10b981'
                : toastNotif.type === 'warning' ? '#f59e0b'
                : toastNotif.type === 'danger' ? '#ef4444'
                : '#0284c7',
              color: '#fff',
              borderBottom: 'none',
              padding: '8px 12px'
            }}
          >
            <i className={`bi ${
              toastNotif.type === 'success' ? 'bi-check-circle-fill'
              : toastNotif.type === 'warning' ? 'bi-exclamation-triangle-fill'
              : toastNotif.type === 'danger' ? 'bi-x-circle-fill'
              : 'bi-info-circle-fill'
            } me-2 fs-6`}></i>
            <strong className="me-auto" style={{ fontSize: '0.85rem' }}>{toastNotif.title || 'Pemberitahuan'}</strong>
            <small style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.7rem' }}>Baru saja</small>
          </Toast.Header>
          <Toast.Body style={{ fontSize: '0.78rem', background: '#fff', color: '#1e293b', padding: '10px 12px' }}>
            {toastNotif.message}
          </Toast.Body>
        </Toast>
      </ToastContainer>

    </div>
  );
};

export default TarifLab;
